/*
 * The seam between the client and the simulation.
 *
 * Everything in this file is a contract, not an implementation: chunk
 * coordinates, the raw vertex buffers a mesher emits, the terrain queries the
 * player's collision needs, and `WorldSource` — the whole simulation as the
 * renderer is allowed to see it. `world/wasm.ts` implements it over
 * `crates/sim-wasm`; until wave 5A a stub with analytic terrain and a
 * JavaScript mesher implemented the same interface, and the swap cost one line
 * in `main.ts` (docs/plans/phase-1-plan.md, wave 5A) — which was the whole point
 * of writing the seam down before there was anything behind it.
 *
 * Two rules keep that swap honest, and both are load-bearing enough to state
 * before the code:
 *
 * - **No gameplay decisions live here or above here.** The client sends
 *   intentions in (interest, dig, and since Phase 5 the player's own deeds) and
 *   consumes events, snapshots and mesh buffers out. The day a rule about the
 *   world is decided in JavaScript, the throwaway renderer stops being
 *   throwaway (devlog #2; plan decision 6). Note what stays true as that list
 *   grows: an intention is a *command*, so everything the player does is in the
 *   replay and nothing they do is decided here.
 * - **No Three.js type may appear in this file.** The interface layer is
 *   engine-agnostic on purpose: it is the same surface a Bevy or Godot client
 *   would consume through `sim-ffi` after Gate A.
 */

/**
 * Tiles along one edge of a chunk.
 *
 * A chunk is 16x16x1 — a single z-slice, not a cube. The z-thickness of 1 is
 * the non-obvious part and it is deliberate: see
 * `docs/research/df-systems-map/systems/map-and-blocks.md` §13.1, "Do not
 * 'improve' it to 16x16x16."
 */
export const CHUNK_SIZE = 16;

/**
 * The shape id a ramp reports through {@link TerrainSampler.tileShape}.
 *
 * DF's `tiletype_shape::RAMP`, which is 10
 * (`docs/research/df-systems-map/systems/map-and-blocks.md` §5.3, transcribed in
 * `sim_core::map::TileShape`). It is the one shape id this client names, because
 * it is the one shape that is not a box: everything else is answered by
 * {@link TerrainSampler.isSolid}, and a ramp is a slope
 * (`player/slope.ts`).
 *
 * Stated here rather than read from the simulation for the reason
 * {@link CHUNK_SIZE} is — a constant behind an async boundary is a constant with
 * a loading state — and checked against `ramp_shape_id()` at startup rather than
 * trusted (`world/wasm.ts`).
 */
export const TILE_SHAPE_RAMP = 10;

/**
 * The shape id a climbing stair reports through {@link TerrainSampler.tileShape}.
 *
 * DF's `tiletype_shape::STAIR_UP`, which is 7 (map-and-blocks.md §5.3,
 * transcribed in `sim_core::map::TileShape`). {@link TILE_SHAPE_RAMP}'s twin,
 * named here for its reason and shipped in the wave the shape was first put in
 * the ground: Phase 7's V-C grades a town's site and puts a **flight** on every
 * one-level step *between built surfaces* where the land would have taken a
 * ramp (`sim_core::townplan`). Both sides of the step have to be something a
 * crew laid — wave 1.6's correction, and the tread rail down a road's flank is
 * what it deleted. A street climbs in steps now, and a client that only knew
 * about ramps would put a walker's feet at the bottom of every one.
 *
 * Only the one that climbs. The two stairs that go down are the *head* of a
 * flight — walkable, floorless, and drawn by nothing — so no body ever stands on
 * one to go up and the client needs no word for them.
 *
 * Checked against `stair_shape_id()` at startup rather than trusted
 * (`world/wasm.ts`), exactly as the ramp is.
 */
export const TILE_SHAPE_STAIR_UP = 7;

/**
 * How many records one chronicle may cite.
 *
 * `sim_core::history::MAX_CHRONICLE_ENTRIES`, stated here for the reason
 * {@link CHUNK_SIZE} is — a constant behind an async boundary is a constant with
 * a loading state — and checked against `max_chronicle_entries()` at startup
 * rather than trusted (`world/wasm.ts`).
 *
 * **The simulation enforces it whatever the client does**: a seventeenth
 * citation is dropped by the type that carries them
 * ({@link WorldSource.placeChronicle}). Knowing the number is what lets a
 * historian UI stop offering pages instead of letting one go missing.
 */
export const MAX_CHRONICLE_ENTRIES = 16;

/**
 * How many ages one Book of Eras carries.
 *
 * `sim_core::history::saga::MAX_SAGA_ERAS`, checked against `max_saga_eras()` at
 * startup like every other copy here. Wave 2B shipped it as the one number on
 * this list the simulation did **not** hand over from a constant of its own —
 * the cap was private to that module and `crates/sim-wasm` transcribed it too —
 * and wave 3A made it public, so what the startup check compares is now this
 * copy against the core's rather than against a second transcription.
 *
 * A client that lays the book out wants it before it has a book: the ages that
 * fall out of a full one are the **oldest**, so a session reading a cut book is
 * reading the world it is standing in and not the whole of history. Nothing
 * crashes if this drifts, which is exactly why it is checked loudly.
 */
export const MAX_SAGA_ERAS = 12;

/**
 * How many records one line of the Book of Eras narrates.
 *
 * One or two: a war narrated *"for the tribute it would not pay"* is a sentence
 * about **two** records, and a trace showing only the war would hide the half
 * that makes it a sentence about cause (Phase 6 decision O6).
 *
 * `sim_core::history::saga::MAX_LINE_CITES`, with one difference from
 * {@link MAX_SAGA_ERAS} worth knowing: **no layout depends on it.** The
 * citations cross behind a per-line count, so a line that grew a third would
 * arrive whole and only this number would be stale. It is here for a trace panel
 * sizing a row, not for a decoder sizing a stride.
 */
export const MAX_SAGA_LINE_CITES = 2;

/**
 * How many lives the birth screen may offer at once.
 *
 * `sim_core::history::origins::MAX_ORIGINS` — four per door across **four**
 * doors since wave 2D-a — checked against `max_origins()` at startup. A start
 * flow that lays the screen out wants it before it has a list, and the
 * simulation never hands over more than this whatever the client believes.
 *
 * **Fewer is the ordinary answer** and often none at all: a young world has no
 * ruined town, no pair of parents old enough and no recorded child young enough.
 * That is the projection working, and the flow falls back to
 * {@link WorldSource.claim}, which needs only a town.
 *
 * **A session's *first* screen still shows twelve**, and the fourth four are the
 * heirs of a line the player has already lost ({@link ORIGIN_HEIR}): the door is
 * empty until somebody has died, so what the number grew is the room a buffer
 * has to leave rather than the length of the menu anybody opens on.
 */
export const MAX_ORIGINS = 16;

/**
 * How many seats one origin card — or one figure — is born holding.
 *
 * `sim_core::history::origins::MAX_CLAIMS`. A parent holds at most the two seats
 * a settlement has, and a pair of them reaches four.
 */
export const MAX_CLAIMS = 4;

/**
 * The door a card came through: the orphan of a town an army came through.
 *
 * `sim_core::history::OriginKind`'s own ids, stated here so the start flow can
 * switch on a number without an async boundary in front of a constant — and
 * checked against `origin_kind_ids()` **and the word each one means** at startup
 * (`world/wasm.ts`), because this switch decides which command a card is handed
 * back to. A renumbered door would send a bound child's figure number through
 * the orphan door's settlement argument, and the simulation would refuse that
 * birth in silence.
 *
 * A card of this kind names no parents and no house: an orphan is nobody the
 * chronicles followed, and the family name is coined when the birth lands.
 */
export const ORIGIN_ORPHAN = 0;

/** A child no chronicle recorded, of two figures it did. {@link ORIGIN_ORPHAN}'s note applies. */
export const ORIGIN_UNRECORDED = 1;

/** A child the chronicles already wrote down. {@link ORIGIN_ORPHAN}'s note applies. */
export const ORIGIN_BOUND = 2;

/**
 * Somebody of the late player's own line. {@link ORIGIN_ORPHAN}'s note applies.
 *
 * **The door permadeath opens** (Phase 7 plan, decision 7): it is empty in every
 * session the player is alive in, and after a death it carries whoever the line
 * has left — at whatever age, which is rider 2's ruling 1. Binding to one is the
 * same command the bound door sends and the same {@link WorldSource.queueBornHeir}
 * shape.
 */
export const ORIGIN_HEIR = 3;

/**
 * How many **bytes** of typed name the simulation's wall keeps.
 *
 * `sim_core::history::PlayerName::MAX_BYTES`. Bytes rather than characters,
 * because that is what the wall promises: a longer name is cut at the last
 * character boundary that fits, never mid-character. A client counting them
 * counts UTF-8 (`new TextEncoder().encode(name).length`), not `name.length` —
 * the two differ for exactly the names where it matters.
 *
 * Checked against `max_player_name_bytes()` at startup, like every other number
 * this file keeps its own copy of.
 */
export const MAX_PLAYER_NAME_BYTES = 32;

/**
 * How many things one pair of hands carries at once.
 *
 * `sim_core::MAX_CARRIED`, checked against `max_carried()` at startup like every
 * other number this file keeps its own copy of. **The simulation enforces it
 * whatever the client believes** — a ninth pickup is refused in silence — so
 * knowing it is what lets the inventory panel say the hands are full instead of
 * letting a verb quietly stop working.
 */
export const MAX_CARRIED = 8;

/**
 * How far a blow reaches, in tiles.
 *
 * `sim_core::STRIKE_REACH_TILES`, checked against `strike_reach_tiles()` at
 * startup like every other number this file keeps its own copy of — and it is
 * the one where the copy earns its keep hardest. The simulation refuses a swing
 * across a longer distance **in silence**, so a client that guessed would offer
 * a strike prompt over people it cannot hit and a key that does nothing three
 * times in four (the graybox ruling's own example).
 *
 * **Chebyshev on the level, and one tile of slack above or below it** — the
 * sim's own predicate, and {@link withinStrikeReach} is where this client
 * applies it. PROVISIONAL sim-side.
 */
export const STRIKE_REACH_TILES = 2;

/**
 * How many ticks a hand waits between swings.
 *
 * `sim_core::STRIKE_COOLDOWN_TICKS`, checked at startup like the reach above.
 * The client uses it for **readiness rather than refusal**: a crosshair that
 * says *ready* and then *a moment* is a player who understands the cadence they
 * are fighting at, where a key that silently does nothing teaches them to stop
 * pressing it.
 *
 * It is the simulation's answer pulse sim-side, so a player and a townsman trade
 * at the same speed.
 */
export const STRIKE_COOLDOWN_TICKS = 60;

/**
 * How far an arrow carries, in tiles.
 *
 * `sim_core::BOW_RANGE_TILES`, checked against `bow_range_tiles()` at startup
 * for {@link STRIKE_REACH_TILES}'s reason, and measured the same way —
 * {@link withinBowRange} is where this client applies it.
 *
 * PROVISIONAL sim-side: DF gives a launcher a force, a velocity cap and four
 * load timings and **no range at all** (items §8.1), so this is a number the
 * host/sim split forces rather than one the study answers.
 */
export const BOW_RANGE_TILES = 12;

/**
 * How many ticks a hand waits between shots.
 *
 * `sim_core::LOOSE_COOLDOWN_TICKS`, which is {@link STRIKE_COOLDOWN_TICKS}
 * exactly — **one pair of hands, one clock** — and is kept as its own name on
 * both sides of the wall anyway, because the day a bow carries its own draw
 * time (§8.1's `beginner_load_time`, the documented trim) this moves and the
 * other does not.
 */
export const LOOSE_COOLDOWN_TICKS = 60;

/**
 * How many people one hero may have at their back.
 *
 * `sim_core::MAX_COMPANIONS`, checked against `max_companions()` at startup like
 * every other number this file keeps its own copy of. The simulation refuses the
 * fifth beckon **in silence**, so knowing it is what lets the prompt say the
 * hero has all the company they can keep instead of letting a key quietly stop
 * working. PROVISIONAL sim-side.
 */
export const MAX_COMPANIONS = 4;

/**
 * How close a hero has to be to take somebody along, in tiles.
 *
 * `sim_core::BECKON_REACH_TILES`, checked against `beckon_reach_tiles()` at
 * startup for {@link STRIKE_REACH_TILES}'s reason and measured the same way.
 * **Derived** sim-side rather than chosen: it is the tier's own definition of
 * *with somebody*, which is the same distance a follower calls arrived at, and
 * it happens to be {@link INTERACT_RANGE_TILES}'s three — but the two are
 * separate numbers that agree, not one number, so the copy is checked like every
 * other.
 */
export const BECKON_REACH_TILES = 3;

/**
 * How many days past a blow the simulation's word about a body can still change.
 *
 * `sim_core::Afflictions::HEAL_DAYS_AT_SLOWEST` — the slowest tissue's own
 * healing span, which is a season of a 336-day year — checked against
 * `heal_days_at_slowest()` at startup like every other number this file keeps a
 * copy of.
 *
 * **It is the only one of these that is a guard rather than an offer.** The
 * others narrow a prompt so the simulation is never asked for something it will
 * refuse; this one bounds a *wait*. A crossing that runs the world until the
 * simulation says the player is up (`host/rest.ts`) has to know when waiting has
 * stopped being the answer, and the honest bound is the span the sim heals on:
 * pain is what is left of the wound, so once this has run every wound is at its
 * floor and a body still under is under for good.
 *
 * PROVISIONAL sim-side in its magnitude, which is exactly why it is read rather
 * than chosen here — the fortnight it replaces was chosen here, and it was
 * three years (chunk Fx-5).
 */
export const HEAL_DAYS_AT_SLOWEST = 84;

/**
 * Returns `true` if two tiles are close enough to take somebody along.
 *
 * {@link withinStrikeReach}'s third call site over its own number
 * ({@link BECKON_REACH_TILES}), and it exists for that function's reason: the
 * simulation refuses a beckon out of reach in silence, so the offer is narrowed
 * by the sim's own predicate before it is drawn.
 */
export function withinBeckonReach(from: TilePos, to: TilePos): boolean {
  return withinTiles(from, to, BECKON_REACH_TILES);
}

/**
 * Returns `true` if two tiles are close enough for a blow to cross.
 *
 * **The simulation's own predicate, applied client-side so a refusal can be
 * spoken before it happens** (`sim_core::combat`'s `within_reach`). It is the
 * one rule this client re-implements rather than asks about, and it is
 * re-implemented rather than exported because it is asked of every drawn body
 * on every frame — a call across the wall per body per frame is the per-entity
 * getter the boundary rule forbids.
 *
 * What keeps the two honest is that the *numbers* cross
 * ({@link STRIKE_REACH_TILES}) and are checked at startup: the shape is four
 * lines and the tuning is the simulation's.
 */
export function withinStrikeReach(from: TilePos, to: TilePos): boolean {
  return withinTiles(from, to, STRIKE_REACH_TILES);
}

/**
 * Returns `true` if two tiles are close enough for an arrow to cross.
 *
 * {@link withinStrikeReach}'s twin over the bow's own number, and one function
 * underneath both — the simulation does exactly the same thing one distance
 * apart (`sim_core::combat`'s `within`), so two copies of four lines here would
 * be two things to keep in step with one.
 */
export function withinBowRange(from: TilePos, to: TilePos): boolean {
  return withinTiles(from, to, BOW_RANGE_TILES);
}

/**
 * Chebyshev on the level, with one tile of slack above and below.
 *
 * The one distance the town layer measures in, and the shape the two predicates
 * above share. Not exported: what a caller wants is *can this blow cross*, and
 * the number that answers it belongs to the weapon rather than to the call site.
 */
function withinTiles(from: TilePos, to: TilePos, tiles: number): boolean {
  const flat = Math.max(Math.abs(from.x - to.x), Math.abs(from.y - to.y));

  return flat <= tiles && Math.abs(from.z - to.z) <= 1;
}

/*
 * What is wrong with a body, as `sim_core::Afflictions`' own bits
 * ({@link WitnessSource.afflictions}).
 *
 * **Every one is a verdict the simulation reached**, crossing as a bit rather
 * than as a number — which is the phase's rule about damage never living
 * host-side, kept at the narrowest place it could be broken. What a client does
 * with them is presentation: a screen that goes dark, a gait that slows, a word
 * at the edge of the frame.
 *
 * Append-only, like every other id here. A bit this build does not know is one a
 * later wave minted.
 */

/** Given in to pain: the body is not acting at all. */
export const AFFLICTED_SENSELESS = 1;

/** No working stance left — the legs will not hold. */
export const AFFLICTED_OFF_ITS_FEET = 2;

/** No working grasp left — nothing can be held. */
export const AFFLICTED_EMPTY_HANDED = 4;

/** Down half the blood the body holds. */
export const AFFLICTED_FAINT = 8;

/**
 * Hurt enough that everything this body does is halved.
 *
 * `sim_core::Afflictions::IN_PAIN`, the lower of the wound model's two pain
 * gates: a body carrying it is awake, on its feet and no good to anybody, where
 * {@link AFFLICTED_SENSELESS} is the higher gate and the screen going dark.
 *
 * It is the bit the rest verb waits on (`host/rest.ts`), and it exists because
 * the condition word cannot answer that question: that ladder reads *crippled*
 * off a lost hand and hides the pain underneath it for good.
 */
export const AFFLICTED_IN_PAIN = 16;

/**
 * The three the rest verb calls *not fit*.
 *
 * **The one composition of the simulation's verdicts this client makes**, and it
 * is a mask rather than a judgement: senseless, going faint, or past the pain
 * gate. Nothing here decides how hurt anybody is — each bit is the simulation's
 * own answer and this only says which of them a player would want to lie down
 * over (chunk Fx-5, the ruling's own list).
 *
 * {@link AFFLICTED_OFF_ITS_FEET} and {@link AFFLICTED_EMPTY_HANDED} are
 * deliberately **not** in it: a lost hand never comes back
 * (`sim_core::health`'s permanent impairment), so a crossing that waited on them
 * would be a crossing that never ended.
 */
export const AFFLICTED_UNFIT = AFFLICTED_SENSELESS | AFFLICTED_FAINT | AFFLICTED_IN_PAIN;

/**
 * What a crossing in the dark asks about the body it is waiting on.
 *
 * Five answers taken together rather than five reads taken apart, because they
 * are asked at one moment about one figure: between two slices of a world being
 * run forward with the screen black, and once a frame while the verb is deciding
 * whether to offer itself (`host/rest.ts`, `main.ts`'s `canRest`). Every one of
 * them is the simulation's own verdict — this shape carries them across the
 * client, and composes nothing.
 *
 * **What it costs a session where nothing is wrong**: one figure read, one
 * affliction mask, one blotter read and one walk of the figure's own biography
 * index, plus the engagement pair over the bodies already on the ground — which
 * is the same walk the stand trigger makes on every fixed step
 * (`witness/witness.ts`'s `needsMyTile`).
 */
export interface RestReading {
  /** Whether the simulation has the body under: {@link AFFLICTED_SENSELESS}. */
  readonly under: boolean;

  /** Whether it would still call the body unfit: {@link AFFLICTED_UNFIT}. */
  readonly unfit: boolean;

  /**
   * Whether anybody in the town has reached the player — engaged with them, or
   * winding up at them.
   *
   * The read the stand stream is armed off (`witness/witness.ts`), spent here on
   * a second question: a rest is a thing you do while nothing is happening, and
   * a hand on the shoulder is something happening.
   */
  readonly reached: boolean;

  /**
   * What the town holds against the player, in the simulation's own word, or
   * `null` for nothing.
   *
   * A word rather than an id because the only thing done with it is said
   * (`hud/crossing.ts`'s `settledWithYou`), and the word is
   * {@link WitnessSource.crimeWord}'s — this client has never kept a crime
   * table.
   */
  readonly wanted: string | null;

  /** The last taking on the player's record, or `null` ({@link WitnessSource.arrestOf}). */
  readonly taken: EventId | null;
}

/**
 * What the reading is for a session nobody has claimed a figure in.
 *
 * Nothing is wrong and nobody is coming, which is the honest answer where there
 * is no body to be asked about — and it makes the rest verb refuse itself in a
 * world with no history rather than crossing one that would never end.
 */
export const NOBODY_RESTING: RestReading = {
  under: false,
  unfit: false,
  reached: false,
  wanted: null,
  taken: null,
};

/**
 * The kinds of work the marketplace has, by `sim_core::JobKind`'s own ids.
 *
 * Stated here so a job scene can pick a working prop from a number without an
 * async boundary in front of a constant ({@link CHUNK_SIZE}'s reason), and
 * checked against `job_kind_ids()` **and the word each one means** at startup
 * (`world/wasm.ts`).
 *
 * Worth checking loudly for {@link TILE_SHAPE_RAMP}'s reason rather than for the
 * birth doors': nothing would crash if one renumbered, and the symptom would be
 * a woodcutter's swing at a seam of rock — a screenshot that looks slightly
 * wrong and nothing worse.
 *
 * The ids are append-only, so one this client does not know is a kind a later
 * build minted: it is drawn as plain work rather than dropped.
 */
export const JOB_MINE = 0;

/** Take a tree down. {@link JOB_MINE}'s note applies. */
export const JOB_FELL = 1;

/** Clear a patch of ground cover. {@link JOB_MINE}'s note applies. */
export const JOB_HARVEST = 2;

/** Work at a smithy's bench. {@link JOB_MINE}'s note applies. */
export const JOB_FORGE = 3;

/** Work at a carpenter's bench. {@link JOB_MINE}'s note applies. */
export const JOB_CARVE = 4;

/** Work at the tavern's kitchen. {@link JOB_MINE}'s note applies. */
export const JOB_COOK = 5;

/** Write a book at the library's desk. {@link JOB_MINE}'s note applies. */
export const JOB_WRITE = 6;

/** Copy one at the same desk. {@link JOB_MINE}'s note applies. */
export const JOB_TRANSCRIBE = 7;

/**
 * Stand a post on the watch's beat. {@link JOB_MINE}'s note applies.
 *
 * The one kind whose site is neither a seam of rock nor a workshop's bench: the
 * simulation posts it at the stations of a derived beat — a gate, the market,
 * the watch house's own door — so what a client draws over it is a guard's post
 * rather than a tool (`witness/scenes.ts`'s `toilVariant`).
 */
export const JOB_PATROL = 8;

/**
 * Lift a body off the ground it fell on. {@link JOB_MINE}'s note applies.
 *
 * The first half of a burial (`sim_core::jobs`, and DF's own `HAUL_BODY` →
 * `PlaceItemInTomb`): the site is the column somebody is lying on, which the
 * item arena answers rather than the terrain.
 */
export const JOB_BURY = 9;

/**
 * Put a body in the ground of a town's burial ground. {@link JOB_MINE}'s note
 * applies.
 *
 * The other half, sited on the cemetery lot. It is what turns a body on a street
 * into a grave with a name cut into it, and only whoever is carrying the body
 * may take it.
 */
export const JOB_INTER = 10;

/**
 * The kinds of item this client draws a heap for, by `sim_core::ItemKind`'s own
 * ids.
 *
 * **DF's `item_type` numbering, gaps and all** (items-and-materials.md appendix
 * B), which is why these are a lookup and never arithmetic: they run 0, 4, 5,
 * 54, 72 and nothing about that is derivable.
 *
 * Five rather than all ten, and the five are the ones a *material family* falls
 * out of: rock, worked metal, timber, growing things. Everything else the world
 * can make draws as a crate, which is what a made thing looks like and is the
 * safe direction for an id a later build minted (`witness/scenes.ts`).
 *
 * Checked against `item_kind_name()` **and the word each one means** at startup
 * (`world/wasm.ts`), for {@link JOB_MINE}'s reason: nothing crashes if one
 * renumbered, and the symptom would be a heap of logs where somebody mined
 * granite.
 */
export const ITEM_BAR = 0;

/** Rough stone: what mining a wall leaves. {@link ITEM_BAR}'s note applies. */
export const ITEM_BOULDER = 4;

/** A felled log. {@link ITEM_BAR}'s note applies. */
export const ITEM_WOOD = 5;

/** What a harvest gathers. {@link ITEM_BAR}'s note applies. */
export const ITEM_PLANT = 54;

/** A meal. {@link ITEM_BAR}'s note applies. */
export const ITEM_FOOD = 72;

/**
 * What is left of somebody. {@link ITEM_BAR}'s note applies.
 *
 * Left on the tile a body fell on (`sim_core::combat`), which is the only
 * producer there is — an abstract death still leaves nothing, because nobody was
 * standing anywhere to leave it.
 */
export const ITEM_CORPSE = 23;

/**
 * A forged blade. {@link ITEM_BAR}'s note applies.
 *
 * The one item kind this client acts on rather than prints, and the reason is
 * the mouse-0 rule (wave 2D-a): a hand with a blade in it pointed at a person is
 * a hand that means it, so what is in the hands decides whether the left button
 * swings or digs (`witness/witness.ts`'s `isFoe`). Checked against
 * `item_kind_ids()` at startup like every other id here.
 */
export const ITEM_WEAPON = 24;

/**
 * Where each ramp rise direction points, indexed by the id
 * {@link TerrainSampler.rampRise} answers with: `+x`, `−x`, `+y`, `−y`.
 *
 * **The order is the rule, not a detail of it.** A ramp rises towards the first
 * of those four in which the neighbouring tile fills its cube sideways
 * (`crates/mesher/src/ramp.rs`), so the list decides which way a ramp in a
 * corner leans, and the mesher walks it in exactly this order when it draws the
 * slope. The client's collision has to agree with the picture, so this table is
 * a transcription and never a preference; `crates/sim-wasm/tests/it/queries.rs`
 * pins it by comparing the reported directions against the normals the mesher
 * actually emits.
 *
 * An id outside the table — which is what `-1` is — means "no slope here":
 * either the tile is not a ramp, or it is a ramp with no wall to lean on, which
 * the mesher draws as a flat floor plate.
 */
export const RAMP_RISE_STEPS: readonly (readonly [number, number])[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/**
 * A tile position in world space. Integer tile coordinates, x/y horizontal and
 * z up — the simulation's convention, kept unswizzled all the way to the GPU
 * (see `render/scene.ts`).
 */
export interface TilePos {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * A chunk address: the chunk's column in `cx`/`cy` and its z-slice in `z`.
 *
 * `z` is a tile coordinate rather than a chunk coordinate because chunks are one
 * tile thick — dividing it by anything would be a lie.
 */
export interface ChunkCoord {
  readonly cx: number;
  readonly cy: number;
  readonly z: number;
}

/**
 * The vertex buffers for one chunk mesh.
 *
 * This is the exact shape `crates/mesher` will emit across the wasm boundary
 * (plan decision 4), which is why it is stated once, here, and guarded by
 * {@link validateChunkMeshBuffers} at every ingest point:
 *
 * - `positions` — 3 floats per vertex, in **world tile units** (x, y, z with z
 *   up), not chunk-local. The mesher already knows the chunk origin; making it
 *   the renderer's job to add it back would be one more place to get it wrong.
 * - `normals` — 3 floats per vertex, unit length.
 * - `colors` — 3 floats per vertex, **linear** RGB in 0..1. Linear because that
 *   is Three.js's working colour space since r152: a material colour read out of
 *   `data/materials.toml` as sRGB bytes is converted by whoever produces the
 *   buffer, so the renderer can upload it untouched.
 * - `indices` — 3 per triangle, counter-clockwise when seen from outside the
 *   solid. Always `Uint32Array`, even when the mesh would fit in 16 bits: one
 *   width for one contract.
 *
 * Arrays handed to a consumer may be views into wasm linear memory, which detach
 * without warning when the sim's heap grows (rust guide §14). Consumers copy on
 * ingest and never retain the array they were given.
 */
export interface ChunkMeshBuffers {
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly colors: Float32Array;
  readonly indices: Uint32Array;
}

/**
 * What the world says about a chunk mesh since the last drain.
 *
 * A discriminated union rather than "set with empty buffers means remove",
 * because an empty chunk (all air, or all interior) is a real and common answer
 * and must not be confused with a chunk that left the player's interest.
 */
export type ChunkMeshUpdate =
  | { readonly kind: "set"; readonly coord: ChunkCoord; readonly buffers: ChunkMeshBuffers }
  | { readonly kind: "remove"; readonly coord: ChunkCoord };

/**
 * The terrain queries host-side collision needs.
 *
 * Phase 1 keeps the player's transform in the client and collides it against the
 * sim's answers (plan decision 7), so this is the whole of the physics contract.
 * It was one question wide until Phase 2 wave 3A gave worldgen ramps to emit:
 * the two shape queries below are what a body needs to walk a slope instead of
 * through it, and they arrived exactly as this comment predicted they would.
 * DF's remaining shape attributes (`walkable_up`, `passable_low/high`; map §5.3)
 * become further methods when stairs and fluids need them.
 */
export interface TerrainSampler {
  /**
   * Returns `true` when the tile blocks a body from occupying it.
   *
   * This is the negation of DF's `passable_flow` attribute
   * (`docs/research/df-systems-map/systems/map-and-blocks.md` §5.3), evaluated
   * for a walking creature, **unioned with "the world has not generated this
   * tile"**.
   *
   * That second half is a rule about the *client*, not about the world, and it
   * is composed here rather than in the simulation on purpose. The sim answers
   * three separate questions — `is_solid`, `is_walkable`, `is_generated` — and
   * says of an absent chunk only that it is absent: "no chunk here" is a fact
   * about the map, and reading it as rock inside the sim would put an invisible
   * wall at the streaming frontier (`crates/sim-wasm/src/terrain.rs`). A player
   * who cannot see the world yet must not fall through it, so the client picks
   * the safe reading and owns it: `!isGenerated || isSolid`
   * (`world/wasm.ts`).
   *
   * Coordinates are integer tile coordinates; callers floor before asking.
   */
  isSolid(x: number, y: number, z: number): boolean;

  /**
   * Returns `true` when the world has actually generated this tile.
   *
   * The third of the questions `crates/sim-wasm/src/terrain.rs` answers, exposed
   * because {@link TerrainSampler.isSolid} deliberately buries it: that method
   * unions "no chunk here" into "solid" so a player cannot fall through the
   * streaming frontier, which is the right answer for a falling body and the
   * wrong one for anything that has to *find* the ground.
   *
   * Concretely: a downward scan for a surface, asked before the first tick has
   * generated anything, meets `isSolid === true` at the very first level it
   * probes and places whatever it was positioning three tiles into the sky
   * (`witness/crowd.ts`'s `surfaceZ`, which is where this was found). Asking the
   * unvarnished question is the fix; composing the safe reading stays the
   * caller's job, exactly as it was.
   */
  isGenerated(x: number, y: number, z: number): boolean;

  /**
   * Returns `true` when the tile is a thing **standing on** the ground rather
   * than the ground itself: a tree's trunk or crown, a town's wall or rampart.
   *
   * `sim_core::Snapshot::stands_on_ground`, and it is a *footing* question
   * rather than a walking one (wave 1.5's graybox ruling). A scan for "how high
   * is the ground here" stops at the first solid tile — and since a trunk's base
   * replaces its column's surface tile, and a house wall has always stood on
   * one, that scan put bodies and props on top of trees and on rooftops
   * ({@link surfaceZ}, which is where both were found).
   *
   * **Asked, never inferred.** What separates a trunk, a crown and a house wall
   * from the rock under them is a variant, a material and a town plan, and none
   * of those is vocabulary this client keeps a copy of (devlog #2).
   *
   * A tree and a wall still *block* a body exactly as they always did —
   * {@link TerrainSampler.isSolid} is the question about that, and it is
   * unchanged.
   */
  standsOnGround(x: number, y: number, z: number): boolean;

  /**
   * Returns the tile's shape id, and `0` where there is no terrain.
   *
   * DF's `tiletype_shape` value (map §5.3, `crates/sim-wasm/src/terrain.rs`).
   * The client matches exactly one of them — {@link TILE_SHAPE_RAMP} — and
   * treats every other id as a box that {@link TerrainSampler.isSolid} has
   * already answered for.
   *
   * `0` is both "this tile has no terrain" and "the world has not generated this
   * tile"; they mean the same thing to a body, which is why nothing here
   * separates them.
   */
  tileShape(x: number, y: number, z: number): number;

  /**
   * Returns which way a ramp at the tile rises, as an index into
   * {@link RAMP_RISE_STEPS}, or `-1` when there is no slope to report.
   *
   * The mesher's rule, translated and not re-derived — see
   * {@link RAMP_RISE_STEPS} for why the order matters and where it is pinned.
   * A tile that is not a ramp answers `-1`, and so does a ramp with no wall
   * beside it; `tileShape` is what tells those two apart, and
   * `player/slope.ts` is the only thing that asks.
   */
  rampRise(x: number, y: number, z: number): number;

  /**
   * Returns the z of a column's walkable surface: the open tile a body stands
   * **in**, resting on the solid one below it.
   *
   * `sim_core::Snapshot::ground_level`, and the one query here that does not
   * read the map. It re-derives the elevation field from `(seed, x, y)`, so it
   * answers for every column in the world including ones no interest has
   * generated — which is exactly what a client needs when it has to place
   * something before the terrain under it exists. `world/wasm.ts` has always
   * used it to choose the spawn point; Phase 3 needs the same answer for every
   * body and every prop, so it joins the sampler rather than staying a private
   * call inside the constructor.
   *
   * **Feet go at this level, not one above it.** `sim_core::worldgen`'s
   * `tile_at` places a floor (or a ramp) at `z == column.ground_z` and solid
   * rock below, so `ground_z` is already the standing level. The wasm
   * declaration's wording ("the walkable floor with open space above it") reads
   * the other way and `createWasmWorld` adds one to it, which is why a session
   * begins with the player falling a single tile onto the ground — harmless,
   * pre-existing, and flagged rather than quietly changed here.
   */
  groundLevel(x: number, y: number): number;
}

/** Numbers the HUD shows and nothing else reads. */
export interface WorldStats {
  /** Simulation ticks elapsed. */
  readonly tick: number;

  /** Chunks the world currently holds meshes for. */
  readonly loadedChunks: number;

  /** Chunks the simulation has generated. Always far larger than `loadedChunks`. */
  readonly generatedChunks: number;

  /**
   * Tiles dug this session, counted from the sim's own `Dug` events.
   *
   * Not from the digs the client *sent*: an illegal dig is dropped inside the
   * tick without a word, and the events are the only way to learn which ones
   * landed (`crates/sim-wasm`'s `queue_dig`). A HUD number that counted
   * intentions would lie in exactly the case a player wants the truth.
   */
  readonly dugTiles: number;

  /**
   * The world hash as a hex string: `0x` and sixteen digits.
   *
   * A string because the real one is a `u64` and JavaScript numbers are not
   * (the replay format makes the same call — plan decision 5). It is the one
   * number on the HUD that proves the simulation is running rather than being
   * imitated, which is why it survived the stub's removal.
   */
  readonly worldHash: string;
}

/**
 * The simulation, as the client is allowed to see it.
 *
 * The method split encodes plan decision 1 — **chunk generation is tick-driven,
 * never read-driven**. Interest and digging are *commands*: they are queued, and
 * they take effect inside {@link WorldSource.step}. Nothing a renderer calls can
 * change what the world contains, which is what makes `(seed, command log)` a
 * complete description of it — and therefore what makes
 * {@link WorldSource.exportReplay} a description of the session rather than a
 * summary of it.
 */
export interface WorldSource {
  /** Terrain queries for host-side collision. Pure; never mutates the world. */
  readonly terrain: TerrainSampler;

  /**
   * Declares which chunks the client wants to see, as a command.
   *
   * Becomes `Command::Interest { center, radius_chunks }` (plan decision 1).
   * Called every step with the player's tile; implementations are expected to
   * ignore a repeat of the interest they already have.
   */
  setInterest(center: TilePos, radiusChunks: number): void;

  /** Queues a dig at a tile. Becomes `Command::Dig { pos }` (plan, wave 3A). */
  dig(pos: TilePos): void;

  /*
   * The player's hands (Phase 7, plan decisions 4 and 5, and O5).
   *
   * **Four more commands, and not one of them is a mechanism the player has to
   * themselves.** A gather is a row on the town's own job board taken through
   * the door its own people bid at; a craft is an order at a bench the town
   * would have posted anyway; a pickup and a drop are one write each to the
   * location union every item in the world lives in. That is the emergence rule
   * at the seam: the client sends an *intention about a tile*, and which of the
   * world's systems answers it is the simulation's business
   * (`CLAUDE.md`).
   *
   * All four keep every property {@link WorldSource.dig} has — queued, applied
   * inside {@link WorldSource.step}, written into the replay — including the
   * one that matters most here: **a verb the simulation refuses is dropped
   * without a word.** There is no error channel out of a tick, so the way to
   * learn whether one landed is to look at the world afterwards
   * ({@link WitnessSource.jobs} for the board, {@link WitnessSource.carried}
   * for the hands).
   *
   * **No distance is checked sim-side, and that is the parity answer rather
   * than a shortcut.** A townsman's bid is scored in cost-in-tiles because the
   * simulation knows where they are standing; it has never known where the
   * player is (Phase 3 decision 10). The cost is paid here, in real walking,
   * before the key is pressed — which is why the reach rules live in this
   * client and nowhere else.
   */

  /**
   * Queues taking the marketplace's work at a tile.
   *
   * Becomes `Command::Gather { pos }`. **There is no mine, fell or harvest
   * method and there is not going to be one**: which of the three a tile is a
   * site for is the simulation's own predicate, the same one that emits a
   * town's work, so a client that decided it would be keeping a copy of a
   * judgement it does not own.
   *
   * Ignored, silently, when no settlement is promoted, when nobody has claimed
   * a figure, when the tile is no kind of work, when somebody already has it,
   * and when the board is full.
   */
  gather(pos: TilePos): void;

  /**
   * Queues taking a workshop's order at a bench.
   *
   * Becomes `Command::Craft { pos, recipe }`. `recipe` is a row number or
   * `null`, and **`null` is the demand rule** — whatever that bench would have
   * taken anyway, most refined first — while a number is a row that has to be
   * runnable there today or the command refuses (`sim_core::RecipeChoice`).
   *
   * The tile is the `work` column of a {@link TownLot}, which is the same tile
   * the marketplace posts its own orders at: the offer a client draws and the
   * command it sends are one row.
   */
  craft(pos: TilePos, recipe: number | null): void;

  /**
   * Queues picking the first thing lying on a tile up.
   *
   * Becomes `Command::Take { pos }`. **The command names a tile and never an
   * item**: the pick rule — the first handle in handle order — is the
   * simulation's, spelled once, because a client naming a handle would be
   * naming something it read a frame ago and a pile that moved under it would
   * put the wrong thing in somebody's hands.
   *
   * Ignored when there is no player figure, when the chunk does not exist, when
   * nothing on the tile is a thing hands lift, and when the hands are already
   * full ({@link MAX_CARRIED}).
   */
  take(pos: TilePos): void;

  /**
   * Queues putting the first thing the player is carrying down on a tile.
   *
   * Becomes `Command::Drop { pos }`: {@link WorldSource.take}'s mirror, with the
   * same pick rule and the same refusals.
   */
  drop(pos: TilePos): void;

  /*
   * Where the player is, and what they are swinging at (Phase 7, wave 2D-a,
   * plan W8 and W11).
   *
   * **The one place the paragraph above stops being true.** Everything else on
   * this surface names a tile because a command cites the work and never the row
   * (plan O5); a strike names a *person*, and the exception is argued in the
   * core's own documentation: nothing ever leaves the figure arena, so a display
   * number belongs to one person for the life of the world, and a tile could not
   * pick between two people standing on one doorstep anyway.
   *
   * It also stops being true that the simulation never knows where the player
   * is. {@link WorldSource.stand} is the whole of what it learns — a tile, held
   * until superseded, **sent only while a blow or a follower needs it** — and it
   * is not a
   * presence: nothing is walked, assigned or dropped at a demotion, and the
   * player's figure still has no body. The narrowness is the point, because a
   * host that streamed its position would be a replay nobody could read.
   */

  /**
   * Queues telling the world which tile the player is standing on.
   *
   * Becomes `Command::Stand { pos }`. **Send it on crossing a tile, and only
   * while somebody needs it** — plus once immediately before every
   * {@link WorldSource.strike}, {@link WorldSource.loose} and
   * {@link WorldSource.beckon}, because a verb aimed from a tile the simulation
   * never heard of is a defect. *Somebody* is two things since wave 2F: a
   * **blow** ({@link WitnessSource.engagedWith},
   * {@link WitnessSource.answering}), and a **follower**
   * ({@link WitnessSource.leaderOf}) — a companion walks after the tile the host
   * states, because the player has no body for the simulation to walk after.
   *
   * Implementations dedupe it, like {@link WorldSource.setInterest} and for its
   * reason: the simulation holds what it was told, so a repeat is not news.
   *
   * Never refused: a tile is a tile, and what it is worth is decided where it
   * is read.
   */
  stand(pos: TilePos): void;

  /**
   * Queues swinging the player's hand at somebody.
   *
   * Becomes `Command::Strike { figure }`. Aim and timing are this client's
   * (plan decision 2); everything from the accuracy roll down is the
   * simulation's, through the same pipeline a watchman and a struck neighbour
   * swing through — and **no damage number and no hit verdict ever crosses
   * back**. What a client reads afterwards is a condition word
   * ({@link WitnessSource.conditionWord}) and a body that may have stopped
   * moving.
   *
   * Ignored, silently, for seven reasons and **every one is a rule this client
   * can apply first**: no player figure, a figure this world has never had, a
   * figure with no body in the promoted town, no {@link WorldSource.stand} ever
   * sent, the two tiles further apart than {@link STRIKE_REACH_TILES}, a swing
   * inside {@link STRIKE_COOLDOWN_TICKS}, and a hand that cannot swing — dead,
   * senseless, empty-handed, or **a child** ({@link WitnessSource.lifeStage},
   * and it is the same row that refuses the child next door).
   */
  strike(figure: FigureId): void;

  /**
   * Queues loosing an arrow at somebody.
   *
   * Becomes `Command::Loose { figure }` — {@link WorldSource.strike}'s twin down
   * to the citation, and it is the *same* exception rather than a second one: a
   * shot names whom it was meant for because a tile could not say which of two
   * people on a doorstep the player aimed at.
   *
   * **Which bow and which arrow is the simulation's pick**, the first of each in
   * the hands, so the host never names a handle it read a frame ago. One arrow
   * is spent per press and lands on the target's tile.
   *
   * Ignored, silently, for {@link WorldSource.strike}'s seven reasons plus two
   * of its own — no bow in the hands, no arrow in the hands — with
   * {@link BOW_RANGE_TILES} in place of the blade's reach and
   * {@link LOOSE_COOLDOWN_TICKS} in place of its cooldown.
   */
  loose(figure: FigureId): void;

  /**
   * Queues putting one carried piece of armour on, either the simulation's pick
   * or a named one.
   *
   * Becomes `Command::Wear { item }`, through the same location union every item
   * move goes through — armour here is not a number, it is a thing between a
   * blow and the skin.
   *
   * **Nothing named is still the default and still means the world's own pick**:
   * the most protective carried thing whose body part is still bare, which is
   * the tile rule taken to its end. Every call site that has ever existed passes
   * nothing and gets exactly what it always got, and the replay keeps the bare
   * word `"wear"` for it so no recorded file moves (`docs/decisions/player.md`
   * §24.5).
   *
   * **A named handle is a display number resolved against the player's own hands
   * and nowhere else** (chunk S-2). Naming one is allowed here for
   * {@link WorldSource.strike}'s reason narrowed: a pile moves under a client's
   * last read because anybody may add to it, and a pair of hands does not — the
   * only thing that changes them is the player, one press at a time — so a
   * handle they no longer hold resolves to **nothing** rather than to something
   * else, and the command does nothing.
   *
   * Ignored when there is no player figure, when nothing wearable is in the
   * hands, when every part it could cover is covered, and when the player is a
   * child — everything this build can wear is armour. A named piece adds three
   * of its own: not a worn kind, no such part, part already covered.
   */
  wear(item?: number | null): void;

  /**
   * Queues holding one carried thing in front — the equip choice.
   *
   * Becomes `Command::Wield { item }`, the ninth command (chunk S-2,
   * `docs/decisions/player.md` §24). **It is one relocation and no new state**:
   * the named thing goes to the head of the carry order, so the pick every
   * reader of that order already makes — the weapon, the bow, the arrow, the
   * drop's tie-break — follows the choice without one of them being edited.
   * `combat::weapon_of` is untouched, which was the door's own condition.
   *
   * The handle is {@link WorldSource.wear}'s named form under exactly the same
   * staleness rule, and it asks nothing about the body or the thing: ordering a
   * pair of hands is not arming them, so a child may wield and still cannot
   * swing, and wielding a boulder is legal — it means the weapon pick walks past
   * the boulder.
   *
   * Ignored when there is no player figure and when the number is not in the
   * player's hands *now*. Wielding what is already wielded is not a refusal: it
   * writes nothing and answers yes.
   */
  wield(item: number): void;

  /**
   * Queues taking one worn piece off, back into the hands.
   *
   * Becomes `Command::Doff`: {@link WorldSource.wear}'s mirror, naming nothing
   * for the same reason, and putting the piece in the hands rather than on the
   * ground — {@link WorldSource.drop} is the press after this one.
   */
  doff(): void;

  /**
   * Queues taking somebody along.
   *
   * Becomes `Command::Beckon { figure }`, and it cites a **person** for
   * {@link WorldSource.strike}'s reason and under the same exception — a third
   * spending of it rather than a new argument.
   *
   * **It confirms an offer the world made.** A figure offers to follow a hero
   * whose deeds have reached them and whose dials lean that way
   * ({@link WitnessSource.followOffer}), so the press is the player saying yes;
   * the beat confirms and never asks, which is the owner's own UI rule.
   *
   * Send {@link WorldSource.stand} immediately before it, exactly as for a
   * swing: the reach is measured from the tile the simulation is holding.
   *
   * Ignored, silently, for six reasons and every one is a rule this client can
   * apply first: no player figure, a figure this world has never had, a figure
   * with no body in the promoted town, no {@link WorldSource.stand} ever sent,
   * the two tiles further apart than {@link BECKON_REACH_TILES}, and anything
   * the offer rule itself refuses.
   */
  beckon(figure: FigureId): void;

  /**
   * Queues sending one of the player's companions on their way.
   *
   * {@link WorldSource.beckon}'s mirror, and thinner on purpose: parting company
   * asks no distance and no dials. Ignored when there is no player figure, when
   * the figure is one this world has never had, and when they follow somebody
   * else.
   */
  dismiss(figure: FigureId): void;

  /*
   * The player's deeds (Phase 5, `docs/plans/player-as-figure.md` §1).
   *
   * **The one amendment to Phase 3's decision 10, and only this one.** The
   * player is still not a sim entity: no position, no journal entry and no
   * conversation crosses back into the simulation, and the body stays here. What
   * crosses is the *record* of what the player did — the same door every
   * figure's deeds land through, so reputation is the rumour layer running with
   * nothing changed in it.
   *
   * All three are commands like {@link WorldSource.dig} and keep every one of
   * its properties: queued rather than applied, taking effect inside
   * {@link WorldSource.step}, and written into the replay — so a session's
   * player is reconstructible from `(seed, command log)` and from nothing else.
   * And like a dig, **a deed the simulation refuses is dropped without a word**:
   * there is no error channel out of a tick, so the way to learn whether one
   * landed is to look at the world afterwards.
   */

  /*
   * The birth door (Phase 6, decisions 3 and 4, and
   * `docs/research/player-fantasies/fantasy-01-born-into-the-world.md` §2).
   *
   * **One command with three shapes, so three methods.** The simulation's
   * selector is a tagged union — an orphan cannot name a mother, a bound child
   * cannot name a town it was not born in — and the boundary keeps that shape
   * rather than flattening it into a `kind` plus three maybe-used numbers, which
   * is a payload a caller can get wrong while every number stays plausible
   * (`crates/sim-wasm/src/sim.rs` argues the split).
   *
   * They keep the `queue` prefix the boundary's own exports have, and only these
   * three do: it is what makes the family read as one thing at a call site next
   * to {@link WorldSource.claim}, which is the *other* door and is unchanged.
   *
   * What a caller passes is what {@link WitnessSource.origins} handed it — a
   * card's `kind` says which of the three, and the handles beside it are that
   * one's arguments. **Nothing here is a card's position in the list.** The
   * screen is derived from the log, so a birth recorded as "the third card"
   * would mean a different life the moment a town was sacked a day earlier, and
   * a replay that means something else on re-run is not a replay.
   *
   * All three are commands like {@link WorldSource.dig}: queued, applied inside
   * {@link WorldSource.step}, written into the replay, and **dropped without a
   * word** when the world does not accept them. Read
   * {@link WitnessSource.playerFigure} on the next step to learn which happened.
   */

  /**
   * Is born as the orphan of a town an army came through.
   *
   * Becomes `Command::Born` with the orphan selector. The figure is created
   * through the same door every figure in the world comes through — a scheduled
   * death, rolled facets, a record of its arrival — and the family name is
   * coined rather than typed (decision 5: inherit-or-coin).
   *
   * `settlement` is the `settlement` of an {@link OriginCard} whose `kind` is
   * {@link ORIGIN_ORPHAN}. The name is normalized by the simulation, exactly as
   * {@link WorldSource.claim}'s is; pass what the player typed.
   *
   * Ignored when the name is nothing but whitespace, when a player figure
   * already exists, and when the town is not one this world has **in ruin
   * today**.
   */
  queueBornOrphan(name: string, settlement: SettlementId, coinNonce?: number): void;

  /**
   * Is born as a child no chronicle recorded, of two people it did.
   *
   * Becomes `Command::Born` with the unrecorded selector: the two figures are
   * written into the log as the player's parents, which is honest for the reason
   * the fantasy states outright — chronicles do not record every commoner birth.
   * Whatever seats those parents held are the player's claims from the moment
   * they are born ({@link WitnessSource.claims}).
   *
   * `mother` and `father` are the columns of an {@link OriginCard} whose `kind`
   * is {@link ORIGIN_UNRECORDED}. There is no town argument because it is not a
   * second fact: both parents live in one settlement or they are not a pair the
   * door accepts.
   *
   * Ignored when the name is empty, when a player figure already exists, and
   * when the pair is not two living grown residents of one town, unrelated to
   * each other and old enough to have had this child.
   */
  queueBornUnrecorded(
    name: string,
    mother: FigureId,
    father: FigureId,
    coinNonce?: number,
  ): void;

  /**
   * Is born as a child the chronicles already wrote down.
   *
   * Becomes `Command::Born` with the bound selector, and it is the door that
   * **creates nothing**: the figure existed before the session started, and
   * their birth record, their scheduled death, their facets and their house are
   * the ones the world gave them years ago. The one write it makes is the name —
   * binding names them — which is why the flow still asks the player to type
   * one.
   *
   * `figure` is the `figure` of an {@link OriginCard} whose `kind` is
   * {@link ORIGIN_BOUND}; that field is `null` on the other two doors, because
   * their person does not exist until the command is sent.
   *
   * Ignored when the name is empty, when a player figure already exists, and
   * when the figure is not somebody the world will hand over: dead, a child,
   * seated, too old, or nobody's recorded child.
   */
  queueBornBound(name: string, figure: FigureId, coinNonce?: number): void;

  /**
   * Is born as somebody of the late player's own line.
   *
   * Becomes `Command::Born` with the heir selector — the fourth door, and the
   * one permadeath opens (Phase 7 plan, decision 7). Like the bound door it
   * **creates nothing**: the kinsman existed before the session started, and
   * binding names them.
   *
   * `figure` is the `figure` of an {@link OriginCard} whose `kind` is
   * {@link ORIGIN_HEIR}.
   *
   * **An heir may be a child**, and that is rider 2's ruling 1 rather than an
   * oversight: the stage capabilities that bind an NPC child bind the player
   * exactly, so a played nine-year-old is refused work beyond chores and refused
   * a fight, by the rows a client reads through
   * {@link WitnessSource.lifeStage}.
   *
   * Ignored when the name is empty, when the player's figure is still **alive**,
   * and when the figure is not living kin of the late one.
   */
  queueBornHeir(name: string, figure: FigureId, coinNonce?: number): void;

  /**
   * Claims a name, and with it a historical figure the player's deeds will cite.
   *
   * Becomes `Command::Claim { name, settlement }` (plan decision 1). The figure
   * enters through the same door every other figure does, so it gets the same
   * invariants — a scheduled death, rolled facets, a record of its arrival — and
   * nothing about it is special-cased afterwards.
   *
   * **The name is normalized by the simulation, not here.** Whitespace is
   * collapsed, control characters dropped, the ends trimmed and the result cut
   * to {@link MAX_PLAYER_NAME_BYTES} at a character boundary — one validation
   * path, sim-side, because the replay format has to agree with it and two
   * normalizations that disagree are a world that cannot be replayed. Pass what
   * the player typed.
   *
   * Ignored, silently, when the name is nothing but whitespace, when a figure
   * has already been claimed, or when `settlement` is nowhere. Read
   * {@link WitnessSource.playerFigure} afterwards to know which happened.
   *
   * **Unchanged by Phase 6 and still the other door** (decision 4 — *two
   * doors*): this is the in-world first-deed moment, where the three
   * `queueBorn*` methods above are the session-start one. A world has one
   * player, so whichever lands first makes the figure and the other is refused —
   * and a step carrying both runs the birth first, whatever order they were
   * queued in.
   */
  claim(name: string, settlement: SettlementId): void;

  /*
   * What a coined family name would be, at one press of *coin another*.
   *
   * **The surname ruling's read half** (owner, 2026-08-25): the family name is
   * the world's to write and the player's agency is a *reroll*, so a birth screen
   * offers another draw and each press submits the same birth with the next
   * nonce. Choosing between draws means seeing them, which is what these three
   * answer.
   *
   * They are **reads, not commands**: nothing is queued, no tick runs, no hash
   * moves and the replay has no idea they were asked. What they cost is a copy of
   * the world — the simulation gets the answer right by running the birth door on
   * a throwaway clone rather than by predicting it — which pre-spawn is the
   * history tier and nothing else, because chunks are generated inside a tick and
   * the birth screen is read before the first one.
   *
   * The empty string is the answer for every birth that **inherits** — a bound
   * child of a standing line, an unrecorded child of housed parents — because no
   * press can rename a house, and for every birth this world would refuse. A
   * screen that shows the button only where one of these answers with something
   * is reading the ruling's own rule off the simulation instead of transcribing
   * it.
   *
   * Three methods for three doors, exactly as the three `queueBorn*` above are
   * three, and for the same reason: the arguments are the door's.
   */

  /** The line the orphan's door would coin. This door always coins. */
  coinedOrphanHouse(name: string, settlement: SettlementId, coinNonce: number): string;

  /** The line the unrecorded child's door would coin, if both parents are houseless. */
  coinedUnrecordedHouse(
    name: string,
    mother: FigureId,
    father: FigureId,
    coinNonce: number,
  ): string;

  /** The line the bound child's door would coin, which is usually nothing at all. */
  coinedBoundHouse(name: string, figure: FigureId, coinNonce: number): string;

  /**
   * Tells a settlement about a record, and cites the player if it was news.
   *
   * Becomes `Command::Tell { settlement, event }` (plan decision 4). The
   * simulation runs the same first-telling-wins learn a caravan runs, and *if
   * and only if* the town genuinely learned something, a record citing the
   * player's figure lands in the log and starts travelling the roads like any
   * other. There is no reward: the deed is the record.
   *
   * So the ordinary outcome is nothing at all — most of what a player knows,
   * the town next door already heard. The share-word flow offers the entries
   * the journal holds that the local store lacks, which is a client-side
   * comparison of two lists the simulation already answers with.
   */
  tell(settlement: SettlementId, event: EventId): void;

  /**
   * Leaves a written chronicle on the ground.
   *
   * Becomes `Command::PlaceChronicle { entries, pos }` (plan decision 5). A book
   * is an ordered list of record ids and **not one word of prose** — the phase's
   * "no player prose enters the world" rule is this signature: the only thing a
   * player can put in the world's mouth is which records it should read out, and
   * what it says when somebody reads it is
   * {@link WitnessSource.chronicleLines}.
   *
   * At most {@link MAX_CHRONICLE_ENTRIES} of them survive, ids the log does not
   * have are dropped, and a book that resolves to nothing is not left. All three
   * are the simulation's rules; nothing here re-applies them.
   *
   * Which town the book belongs to is the simulation's answer — the nearest one
   * within reach of `pos`. The simulation still does not learn where the
   * *player* is standing: what it learns is where a book is.
   */
  placeChronicle(entries: readonly EventId[], pos: TilePos): void;

  /**
   * Advances the world by exactly one fixed tick, consuming queued commands.
   *
   * Called from the host loop's fixed step at `TICK_HZ` (`host/timestep.ts`) and
   * from nowhere else. Takes no time argument: the sim knows ticks and nothing
   * else (rust guide §10.2).
   */
  step(): void;

  /**
   * Returns and clears the mesh changes accumulated since the last call.
   *
   * Drained once per rendered frame, so several ticks' worth coalesce into one
   * upload pass. The returned buffers are only valid until the next
   * {@link WorldSource.step} — see {@link ChunkMeshBuffers}.
   */
  takeMeshUpdates(): ChunkMeshUpdate[];

  /**
   * The tiles the ticks since the last call changed, drained.
   *
   * `Event::Dug`, as the client is allowed to see it: one entry per tile a dig
   * or a completed piece of collection work opened, in emission order. Drained
   * once a frame like {@link WorldSource.takeMeshUpdates}, so several ticks'
   * worth coalesce.
   *
   * **This is how a renderer learns where to look**, and the simulation says so
   * itself: there is deliberately no *"every pile in the world"* iterator, and a
   * scan of the map would be the expensive way to ask a question nobody has
   * (`sim_core::Snapshot::items_on`). A client that wants to draw what is lying
   * about watches these tiles and the ones it already has a reason to know
   * about, and asks {@link WitnessSource.tileItems} of those.
   */
  takeDugTiles(): readonly TilePos[];

  /** Diagnostics for the HUD. */
  stats(): WorldStats;

  /**
   * Returns the session so far as the text of a replay file.
   *
   * `(seed, bootstrap, command log, hash sequence)` as JSON — the same file
   * `sim-cli record` writes and `sim-cli verify` re-runs (plan decision 5). The
   * client's only job is to hand the bytes to whoever asked; it neither parses
   * nor understands them, and the round trip that matters happens outside the
   * browser. The format's version number is the format's business and this
   * client has never read it.
   *
   * A read, not an ending: the session keeps running and a later call describes
   * the longer run.
   *
   * @throws if a command submitted this session has no spelling in the replay
   * format, which means the simulation grew a verb the format has not been
   * taught (`crates/sim-wasm/src/record.rs`). Every verb on this interface has
   * one — including the three player deeds — and the guard is that the boundary
   * refuses to hand over a file describing a run nobody performed rather than
   * writing a quietly incomplete one.
   */
  exportReplay(): string;
}

/*
 * The witnessing surface: what the world says about itself.
 *
 * Phase 3 gives the client a second thing to read out of the simulation, and it
 * is a different *shape* from the first. {@link WorldSource} is terrain and
 * commands at tick rate; everything below is slow, small and pulled — the
 * visited settlement's living named figures, its market, the rumours it holds,
 * the aftermath the log has left on the ground, and the roads news travels
 * along (docs/plans/phase-3-plan.md, decisions 8–11).
 *
 * Three rules shape it, and each is a plan decision rather than a convenience:
 *
 * - **A rumour is a reference, never a copy** (decision 2). Every channel
 *   carries an {@link EventId} into the history log and nothing here duplicates
 *   a payload field. That is what makes the phase's exit criterion — three
 *   independent channels traceable to the same records — a question a debugger
 *   answers rather than a claim a designer makes.
 * - **Strings cross per call** (decision 11). Names, spoken lines and
 *   inscriptions are methods, not fields: they are pulled on an interaction or
 *   at a day boundary, never once per tick. Bulk numbers would cross as flat
 *   typed arrays; nothing on this surface is bulk yet, and the day it is, it
 *   changes shape rather than growing a thousand-element array of objects.
 * - **The simulation never learns where an NPC is standing** (decision 8).
 *   Identity and knowledge are the sim's; embodiment is the client's. There is
 *   no position on a roster entry, and there is not going to be one.
 *
 * Wave 1B implemented this against hand-authored fixture data
 * (`world/fixture.ts`, dev-only, behind `?fixture=1`) so that every visual and
 * every interaction could be judged on screen before the wasm boundary grew.
 * Wave 4A implements the same interface over `sim-wasm`
 * (`world/wasm.ts`'s `WasmWitnessSource`) and that is what a session gets; the
 * fixture stays as the dev-flag fallback, because a visual that needs a
 * particular war two towns away is faster to judge against data somebody chose
 * than against a seed somebody has to find. Nothing above this file learns which
 * one it is talking to, which was the whole point of writing the interface down
 * first.
 */

/**
 * One record's sequence number in the history log.
 *
 * A `number` rather than the string {@link WorldStats.worldHash} is, and the
 * difference is not inconsistency: `EventId` is `u64` in `sim-core` but it is a
 * *counter* starting at zero, so it fits a JavaScript integer for any run that
 * could physically exist, while a hash uses the whole 64-bit range on its first
 * value. Keeping it a number is what lets it be a `Map` key, a sort key and a
 * debug-view lookup without a parse at every use.
 */
export type EventId = number;

/**
 * A named figure, as the slot number `FigureId::number()` reports.
 *
 * The small number a legends line calls a figure, not the generational key —
 * that key does not cross the boundary, because the client never resolves a
 * record itself. Two figures that held the same arena slot in turn share a
 * number (`sim_core::history::arenas`); this client displays names and never
 * asks the number to be unique across time, only across a session's roster.
 */
export type FigureId = number;

/** A settlement, as the slot number `SettlementId::number()` reports. */
export type SettlementId = number;

/**
 * A house — a family name that outlives whoever carries it — as the slot number
 * `HouseId::number()` reports.
 *
 * The third arena, and the last to reach this seam: it arrives with the birth
 * screen, because a card is the first surface that says which line a life is
 * born into ({@link OriginCard.house}). The three arenas number their slots
 * independently, so *house #1* and *figure #1* are not related — the field a
 * number came off is what picks the arena.
 */
export type HouseId = number;

/**
 * A calendar date: the year, and the day within it.
 *
 * `sim_core::calendar::WorldDate`'s two fields and nothing else. The client
 * never does calendar arithmetic on it — comparing two dates is comparing
 * `year` then `dayOfYear`, which needs no knowledge of how long a year is, and
 * anything harder than that is a question for the simulation that owns the
 * calendar.
 */
export interface WorldDateView {
  readonly year: number;
  readonly dayOfYear: number;
}

/**
 * The four personality facets a figure is rolled with.
 *
 * `units-and-ai.md` Appendix B's explicit trim of DF's fifty-value personality
 * down to the handful that pay for themselves, sized by §8.2 (plan decision 5).
 * Each is 0–100.
 *
 * **They colour delivery and nothing else this phase** (units §8.1 item 6): no
 * simulation decision reads a facet, so an anxious speaker says the same news
 * in a more frightened way and the world turns out identically either way.
 */
export interface Facets {
  readonly anxiety: number;
  readonly bravery: number;
  readonly trust: number;
  readonly gregariousness: number;
}

/**
 * One living named figure of the visited settlement.
 *
 * The roster is `FigureId`-stable across a session, which is what Phase 4's
 * trader anchor will need (plan decision 8). No position: see the header.
 */
export interface RosterEntry {
  readonly figure: FigureId;
  readonly name: string;
  readonly settlement: SettlementId;
  readonly facets: Facets;
}

/**
 * A settlement on the world map.
 *
 * `x`/`y` are world tile coordinates — the same frame as everything else in
 * this file — because the only thing the client does with them is draw a route
 * as a straight line between two of them (plan decision 8: roads have no
 * geometry).
 */
export interface SettlementView {
  readonly settlement: SettlementId;
  readonly name: string;
  readonly x: number;
  readonly y: number;
}

/**
 * One row of a settlement's market board: a commodity, its price and its band.
 *
 * Prices are derived and never stored (plan decision 7), so this is a read of
 * `price_index` and the stock band at the moment it is asked, plus the latest
 * `PriceShock` that cited the commodity. **{@link MarketShock.cause} is the
 * channel's traceability** — the whole reason a board is a diegetic channel
 * rather than decoration is that the number moved *because* of a record the
 * player can go and find.
 *
 * The names travel with the row rather than being looked up client-side: the
 * display tables are `sim-core`'s (decision 7 promotes them out of `sim-cli`),
 * and a market read is a handful of rows pulled on an interaction, which is
 * exactly the budget decision 11 gives a string.
 */
export interface MarketRow {
  /** `Commodity::id()`. A rendering switch may key off it; nothing else may. */
  readonly commodity: number;
  readonly commodityName: string;

  /** `PRICE_BASE / (1 + months in store)`. Comparable only against itself. */
  readonly priceIndex: number;

  /** `StockBand`'s ordinal: famine, dear, steady, plenty. */
  readonly band: number;
  readonly bandName: string;

  /** The latest shock citing this commodity here, or `null` if there is none. */
  readonly shock: MarketShock | null;
}

/**
 * How deep a settlement's store of something is, by `sim_core::StockBand`'s own
 * ids — driest first, because the ids are ordered the way the thresholds are.
 *
 * Stated here for {@link JOB_MINE}'s reason and checked at startup the same way
 * (`world/wasm.ts`), against `stock_band_name` — **both halves**, so a build
 * that kept four bands and reordered them fails loudly instead of painting a
 * famine in the colour of a full granary.
 *
 * The one thing a client may do with them is *render*: the words are the
 * simulation's ({@link MarketRow.bandName}, and there is no fifth), and what the
 * id buys is which of the kit's four tones the word is set in
 * (`hud/panels/market.ts`'s `bandTone`) — the same licence
 * {@link MarketRow.commodity} carries one field up.
 */
export const BAND_FAMINE = 0;

/** Less than the settlement is comfortable with. {@link BAND_FAMINE}'s note applies. */
export const BAND_DEAR = 1;

/** Enough to eat, not enough to ship. {@link BAND_FAMINE}'s note applies. */
export const BAND_STEADY = 2;

/** More than a settlement will ever need at once. {@link BAND_FAMINE}'s note applies. */
export const BAND_PLENTY = 3;

/** The `PriceShock` behind a market row, and the record it cites. */
export interface MarketShock {
  /** The shock's own record: `PriceShock` is an event like any other. */
  readonly event: EventId;

  /**
   * The record that emptied or filled the store, or `null` where nothing is to
   * blame.
   *
   * `PriceShock::cause` verbatim — an `Option<EventId>` in `sim-core`, and the
   * one field on this surface that makes the market a *channel* rather than a
   * gauge (plan decision 7: "the `cause: Option<EventId>` link is the channel's
   * traceability"). A shock with a cause is the war arriving as a number; a
   * shock without one is weather, which the record's own doc comment calls "a
   * settlement growing into its land".
   *
   * The distinction is worth keeping visible rather than flattening, because it
   * is exactly what the exit criterion counts: three channels carrying **the
   * same war's** records, and a price that moved for no stated reason carries
   * nothing at all.
   */
  readonly cause: EventId | null;

  /** `PriceMove::id()`: rose or fell. */
  readonly move: number;
  readonly moveName: string;
  readonly when: WorldDateView;
}

/**
 * One rumour in a settlement's store.
 *
 * The whole record, and it is four fields on purpose (plan decision 2):
 * `Rumor { event, learned, hops, via }`. There is no text and no copy of the
 * event's payload — the docs correction is binding, DF distorts nothing by
 * hops (history §7.7's news horizon, §11.1's timestamped believed-vs-true
 * facts). What vagueness a player hears is applied by the *spoken projection*
 * ({@link WitnessSource.speak}) from `hops` and age, over a record that stayed
 * true the whole way.
 */
export interface RumorEntry {
  readonly event: EventId;

  /** When this settlement learned it. */
  readonly learned: WorldDateView;

  /** How many settlements the news passed through. Zero is self-knowledge. */
  readonly hops: number;

  /** Who it came from, or `null` when the settlement saw it happen. */
  readonly via: SettlementId | null;
}

/**
 * Something the history log has left standing on the ground.
 *
 * Derived, non-blocking, realized on approach (plan decision 6, world-gen
 * §10.5): a pure function of `(seed, log)` yields these, nothing writes a tile,
 * nothing hashes, and nothing collides. `refs` is the traceability — a grave
 * cites the `FigureDied` record its name and date came off, a burned farmstead
 * cites the sack.
 */
export interface AftermathFeature {
  /** Stable within a session, so a prop can be keyed by it. */
  readonly id: number;

  /** One of the `FEATURE_*` ids below. */
  readonly kind: number;

  /** Where it stands, in world tiles. */
  readonly position: TilePos;

  /** Which way it faces, in radians, in the client's yaw convention. */
  readonly facing: number;

  /** The records it was derived from. Never empty. */
  readonly refs: readonly EventId[];
}

/*
 * The aftermath feature kinds.
 *
 * Stated here so the renderer has a stable discriminant to switch geometry on
 * without an async boundary in front of a constant, exactly as {@link CHUNK_SIZE}
 * and {@link TILE_SHAPE_RAMP} are — and, like those two, **checked against the
 * simulation's own ids at startup rather than trusted**
 * (`world/wasm.ts`'s `checkFeatureKinds`, against `feature_kind_ids()`). Wave 1B
 * wrote them down as a guess at an enum `sim-core` had not grown yet and asked
 * wave 4A to check them; 4A did, and the first three are
 * `sim_core::history::aftermath::FeatureKind`'s own numbers.
 *
 * The fourth is different in kind and the check treats it that way: a market
 * board is not something the log left on the ground, it is the market's physical
 * read, and the client places it. So {@link FEATURE_MARKET_BOARD} is the
 * client's own id, and what the startup check guards is the simulation growing a
 * fourth kind of aftermath that *takes* the number.
 */

/** A named burial with a date: the minimal `state_profilest` model, history §6.2. */
export const FEATURE_GRAVE = 0;

/** A farmstead burned on the route out of a sacked settlement (`WG_RUINED`, buildings §10.2). */
export const FEATURE_BURNED_FARMSTEAD = 1;

/** A marker where a battle was fought. */
export const FEATURE_BATTLEFIELD = 2;

/**
 * The settlement's market board.
 *
 * Not aftermath — it is the market's physical read — but it is placed by the
 * same query and drawn by the same registry, and giving it its own placement
 * path would be two mechanisms for "a thing standing in the world".
 */
export const FEATURE_MARKET_BOARD = 3;

/** One leg of the trade network, as the client draws it: a straight line. */
export interface RouteLeg {
  readonly from: SettlementId;
  readonly to: SettlementId;
}

/**
 * A group of people walking away from a war that really happened.
 *
 * Group granularity, never individuals (history §8.4 — DF's refugee check moves
 * populations, not people), and the group carries the records it is fleeing so
 * that arriving at the refuge *is* the news arriving (plan decision 4).
 */
export interface RefugeeGroup {
  readonly id: number;
  readonly from: SettlementId;
  readonly to: SettlementId;

  /** How many people, in the abstract tier's own counting. */
  readonly size: number;

  /** When they left, which is what the client's walk fraction is measured from. */
  readonly departed: WorldDateView;

  /** The battle or sack they are fleeing. Never empty. */
  readonly refs: readonly EventId[];
}

/**
 * One turn's trade between two settlements, as the client is allowed to see it.
 *
 * `TradePulse` is computed transiently inside `economy::entity_turns` and
 * dropped after it is applied; Phase 4 decision 2 gives the simulation a read
 * seam for the current turn's pulses so the word can gain a body. **No caravan
 * is on screen any more**: the cart the client used to walk along the straight
 * chord between two centres was retired once towns grew wider than its halt and
 * roads stopped following the chord; origin, destination and day stay sim truth,
 * read on the board and by the director, and the body returns as a column that
 * walks the real road (the columns-on-roads seam).
 *
 * The talker is the other half of decision 2: a pulse carries rumours as well as
 * stock, so there is somebody walking with it who can be asked, and they are a
 * *real roster figure of the origin settlement* rather than a voice. Their name
 * travels with the pulse rather than being looked up, because
 * {@link WitnessSource.roster} is the **visited** settlement's living figures
 * and a caravan's talker is by construction from somewhere else.
 *
 * Flat-array-friendly for wave 2A (Phase 3 decision 11): every field is a number
 * except the name, so a pulse row is `[id, from, to, year, dayOfYear, talker]`
 * with the name pulled per call like every other string.
 */
export interface TradePulse {
  /**
   * Stable for as long as the pulse is on the road, so a prop can be keyed by
   * it.
   *
   * The sim's own handle for the turn's pulse — not an index into whatever order
   * the list came back in, because the list is re-read on every day boundary and
   * a body keyed by a position in it would swap places with its neighbour.
   */
  readonly id: number;

  readonly from: SettlementId;
  readonly to: SettlementId;

  /** The day the pulse was computed. The client's walk fraction measures from it. */
  readonly day: WorldDateView;

  /** The figure walking with it, or `null` when the origin has nobody left to send. */
  readonly talker: FigureId | null;

  /** Their name, as the origin settlement's roster spells it. Empty with no talker. */
  readonly talkerName: string;
}

/**
 * Where a spotlight beat surfaces, and therefore what kind of thing it is.
 *
 * Phase 4 decision 1's four channels, stated here for the reason
 * {@link CHUNK_SIZE} and the `FEATURE_*` kinds are: a rendering switch needs a
 * discriminant without an async boundary in front of it. **Wave 1B wrote these
 * down as the client's half of a projection `sim-core` has not grown yet**, the
 * same way wave 1B of Phase 3 wrote down the feature kinds — wave 2A checks them
 * against `spotlight`'s own ids rather than trusting them.
 *
 * 2A did, and they are `sim_core::history::spotlight::Surface`'s own numbers
 * (`world/wasm.ts`'s `checkBeatSurfaces`, against `beat_surface_ids()` and
 * `beat_surface_name()`). Both halves of that check are load-bearing: the ids
 * say the client's four numbers are the simulation's four numbers, and the
 * *names* say each one still means the place it is named after — an id that
 * stayed put while the ordering moved under it would put a body on a road where
 * a price moved on a board, and nothing would crash.
 *
 * They are deliberately coarser than what the simulation calls a *channel*.
 * `Surface` is where a player meets material and there are four of them;
 * `Channel` is what the material is and there are seven — a caravan and a
 * refugee column are two kinds of thing arriving on the same road, a grave and a
 * burned steading are two kinds of mark on the same ground. The client switches
 * on the place, because the place is what it has to draw; what the material
 * *is* reaches it through the records the beat cites.
 */

/** A body on a road: a refugee column, a caravan. */
export const BEAT_ROAD = 0;

/** A number on the market board. */
export const BEAT_BOARD = 1;

/** Something somebody in the street will say if asked. */
export const BEAT_MOUTH = 2;

/** Something the log left standing where it can be walked up to. */
export const BEAT_GROUND = 3;

/**
 * One witnessable beat: material the simulation says is worth meeting.
 *
 * The sim half of Phase 4 decision 1 — **salience is sim-side, scheduling is
 * client-side**. `spotlight` answers "for settlement S, which witnessable beats
 * exist" as a pure projection over the log, the rumour stores, the routes, the
 * market and the aftermath; the client's director consumes them plus the two
 * inputs the simulation can never hold (where the player's body is, and what
 * their journal already contains) and times *presentation only*.
 *
 * Three rules ride on this shape and each is a plan rule rather than a taste:
 *
 * - **The client never re-ranks beats.** The order the source returns them in is
 *   a judgement about what is worth showing, and re-sorting it in TypeScript is
 *   the gameplay-decision-in-JS bug by definition (devlog #2).
 * - **Nothing surfaces before {@link SpotlightBeat.earliest}.** A beat may be
 *   returned with a date in the future — that is what makes the field mean
 *   anything — and a client that staged it early would be pre-echoing a record
 *   that has not landed.
 * - **A beat carries its records.** {@link SpotlightBeat.refs} is what the trace
 *   view shows and what the journal files, so a staged beat is checkable against
 *   the log exactly like every other channel.
 *
 * Flat-array-friendly for wave 2A: everything except `refs` is a scalar, and the
 * client's only requirement of the flattening is that a beat's records arrive
 * together — either a variable stride `[id, channel, subject, year, day,
 * refCount, ...refs]`, or one fixed-stride row per record sharing an `id`.
 */
export interface SpotlightBeat {
  /**
   * Stable within a session and unique across settlements, so a client can
   * remember it has already staged one.
   *
   * Across settlements matters as much as across days: the client keeps the ids
   * it has staged for the life of a session, and a session in which the player
   * walks to the next town must not find that town's beat already spoken for.
   */
  readonly id: number;

  /** One of the `BEAT_*` channels above. */
  readonly channel: number;

  /**
   * The settlement the beat is *about*, or `null` when it is about nowhere in
   * particular — and **"about" means something different on every surface**, so
   * the client may not treat it as a target.
   *
   * Not where it surfaces — that is the settlement the beat was asked for. What
   * it *is* varies with the channel (wave 3A read the live projection and
   * corrected this doc): a road beat's subject is the column's or caravan's
   * **origin**; a mouth beat's is **who told us** (the rumor's `via`); a price
   * beat's is the first settlement its cited record names — for a battle, the
   * **attacker**. None of those is reliably "the town that is burning", which is
   * why `witness/witness.ts` draws smoke only where a refugee column's own
   * records corroborate the claim, and `witness/staging.ts` treats subject as
   * "somewhere else", never as a bearing to point at. A `struck`/defender
   * handle on the beat is the flagged boundary extension if honest pointing is
   * ever wanted on the other surfaces.
   */
  readonly subject: SettlementId | null;

  /**
   * The earliest date the beat can honestly surface.
   *
   * The record's own date where the settlement saw it happen, the date the news
   * arrived where it travelled, the date the price moved where it is a price.
   * Compared against {@link WitnessSource.date} by year and then day — the
   * client owns no calendar arithmetic ({@link WorldDateView}).
   */
  readonly earliest: WorldDateView;

  /** The records behind it. Never empty. */
  readonly refs: readonly EventId[];
}

/**
 * One line spoken by one figure, about one record.
 *
 * The second projection over the same event, never an edit to `narrate()`
 * (plan decision 9, history §1: viewers own no data). `about` is what a journal
 * entry holds afterwards — the player remembers *which record they were told
 * about*, not the sentence they were told it in (decision 10).
 */
export interface SpokenLine {
  /** Who said it, or `null` when the speaker is a group rather than a person. */
  readonly figure: FigureId | null;

  /** What to put above the line: a name, or "refugees from Stonehearth". */
  readonly speaker: string;
  readonly text: string;

  /** The record the line is about, or `null` when the speaker had no news. */
  readonly about: EventId | null;

  /** How far the news travelled to reach this speaker. */
  readonly hops: number;

  /** When this speaker's settlement learned it, or `null` with no news. */
  readonly learned: WorldDateView | null;
}

/**
 * One book standing in a settlement, as the client is allowed to see it.
 *
 * **A chronicle is sim state; the prose is not** (Phase 5 decision 5). Every
 * field here is where the book is and who left it; what it *says* is
 * {@link WitnessSource.chronicleLines}, composed by `sim-core` out of the
 * records it cites. There is no text on this shape and there is not going to be
 * one — the same rule that keeps a spoken line from being assembled in
 * JavaScript (devlog #2).
 *
 * The author may be somebody long dead: the simulation leaves books of its own
 * when a war ends, so a world nobody has played in still has them. That is the
 * NPC parity test on this verb — the player is one more figure writing things
 * down — and it is why a first-time player has something to find.
 */
export interface ChronicleView {
  /** The simulation's own handle, stable for the life of the world. */
  readonly id: number;

  /** Who compiled it. */
  readonly author: FigureId;

  /** Their name, as the log spells it — pulled with the list like a talker's. */
  readonly authorName: string;

  /** Whose shelf it is on: the nearest town to where it was left. */
  readonly settlement: SettlementId;

  /**
   * Where it lies, z included.
   *
   * The one thing on this surface with a *tile* of its own rather than a column
   * the ground is looked up for — a book left in a cellar is not a book on the
   * grass — so a prop goes exactly here.
   */
  readonly position: TilePos;

  /** The day it was left. */
  readonly left: WorldDateView;

  /**
   * How many records it cites, which is how many lines it reads out to.
   *
   * A count rather than the ids, because a shelf is drawn far more often than a
   * book is opened: the ids and the pages both cross per book, on the
   * interaction that opens one ({@link WitnessSource.chronicleEntries},
   * {@link WitnessSource.chronicleLines}), and both come back this long.
   */
  readonly entries: number;
}

/**
 * A town still standing in the wreckage of the last army through it.
 *
 * Phase 5 decision 11's interim honesty: a projection over the log — a recent
 * sack with no recovery since — that lets the client draw ruin props, thin the
 * crowd and quieten the market without the simulation pretending it holds
 * structural damage in world data. Real damage is Phase 7's, per the owner's
 * recorded art direction; **nothing about this flag is stored or hashed**, so a
 * build that draws ruin differently draws *ruin* differently and no replay can
 * tell.
 *
 * {@link RuinView.sack} is the traceability every surface in this project is
 * held to: the ruin cites the battle that caused it, which is the same
 * {@link EventId} the town's own rumour store carries.
 */
export interface RuinView {
  readonly settlement: SettlementId;

  /** The battle that did it. */
  readonly sack: EventId;

  /** The day it was fought, which is when the town started looking like this. */
  readonly since: WorldDateView;
}

/**
 * One sentence of the Book of Eras, and the records it narrates.
 *
 * **The trace contract** (Phase 6 decision O6): a line is never on its own.
 * {@link SagaLine.cites} is never empty and every id in it resolves in the log,
 * which is what turns *"the saga narrates records that exist, never invents"*
 * from a claim into something a debug view demonstrates — the same pairing a
 * chronicle's pages and citations have ({@link WitnessSource.chronicleEntries}),
 * one level deeper because a saga line may narrate two records rather than one.
 *
 * The text arrives whole, composed by `sim-core` in the one register in this
 * build that is *written* rather than said. Nothing above this seam assembles a
 * sentence about the world — the failure devlog #2 exists to name.
 */
export interface SagaLine {
  /**
   * Which slot of the chain composed it: `0` the opener, `1` a body, `2` the
   * close.
   *
   * `sim_core::history::saga::Slot`'s own discriminants, which that type's
   * documentation says a boundary passes as integers. They are append-only and
   * nothing hashes them, so **an id this client does not know is a slot a later
   * build minted** and is to be drawn as text rather than dropped.
   *
   * Carried so a client can lay a chapter out as a paragraph — which sentence is
   * the frame and which are the history — and for nothing else.
   */
  readonly slot: number;

  /** The sentence, exactly as the simulation composed it. */
  readonly text: string;

  /**
   * The records behind it, oldest first. **Never empty**, and at most
   * {@link MAX_SAGA_LINE_CITES} of them.
   */
  readonly cites: readonly EventId[];
}

/**
 * One age of the world, as the book keeps it.
 *
 * Phase 6 decision 2 and O5: an age breaks when a war ends having toppled or
 * founded a ruling line, or when a settlement with a history behind it falls.
 * Derived on the call and never stored, like every other projection on this
 * surface — a build that segments the world differently segments a *book*
 * differently, and no replay can tell.
 *
 * **A quiet seed gets one age, honestly.** That is the rule working rather than
 * the rule failing, and a client that treated one chapter as an empty answer
 * would be hiding the only history such a world has.
 */
export interface SagaEra {
  /**
   * Which age it is, counting from one — **and not this era's position in
   * {@link WitnessSource.saga}'s array.**
   *
   * The two differ the moment a book is cut: the ages that fall out of a full
   * one are the oldest, and the ordinals stay true, so a cut book opens on *the
   * fourth age* at index zero. It is what a page's heading says and never an
   * index into anything.
   */
  readonly ordinal: number;

  /** The record the age is named after and turns on. Resolves in the log. */
  readonly anchor: EventId;

  /** The day it began, which is that record's own day. */
  readonly began: WorldDateView;

  /** The day it ended, or `null` for the age the reader is standing in. */
  readonly ended: WorldDateView | null;

  /**
   * What a book calls it by its number: *the second age*.
   *
   * **Both names cross, because which one a page leads with is an open owner
   * taste call** (Phase 6 plan, open questions: *"do eras get generated names …
   * or ordinals"*). `sim-core` composes both precisely so the choice can be made
   * by looking at it, so a client shows both until it is ruled — and neither is
   * ever assembled here out of {@link SagaEra.ordinal} and a word.
   */
  readonly title: string;

  /** What a book calls it by what happened: *the years after the fall of Barfell*. */
  readonly name: string;

  /** The opener, the bodies and the close, in reading order. */
  readonly lines: readonly SagaLine[];
}

/**
 * A seat one of somebody's parents held.
 *
 * *Child of a figure who held the seat of X* — the claim a noble origin is born
 * with, and the whole of what a claim is this phase (Phase 6 cut list: claims
 * ship as a **read-only projection**, nothing presses one, and no war is caused
 * by one).
 *
 * Like every other channel on this surface it is a reference and its provenance
 * rather than a copy: {@link OriginClaim.cites} is the record that seated the
 * parent, so the sentence a card shows about a claim is
 * {@link WitnessSource.describe}'s and never one the client assembled.
 */
export interface OriginClaim {
  /** The parent who held it. */
  readonly parent: FigureId;

  /** What the log calls them, with whatever byname their own records earned. */
  readonly parentName: string;

  /** The settlement whose seat it is. */
  readonly settlement: SettlementId;

  /**
   * What that settlement is called.
   *
   * Carried on the row rather than left to a lookup, like
   * {@link OriginCard.settlementName} and a book's `authorName`: a seat may be
   * in a town the reader has never been to, and the screen this feeds is drawn
   * before there is a map on screen to look one up in.
   */
  readonly settlementName: string;

  /**
   * Which seat: `sim_core::history::PositionKind`'s own id — ruler `0`, hero
   * `1`.
   *
   * **This build ships no name for it, deliberately.** A seat's *sentence* is
   * the record it cites, and an id is what a client groups or ranks by; the ids
   * are append-only, so one this client does not know is a seat a later build
   * minted and is to be shown by its record rather than dropped.
   */
  readonly position: number;

  /** The record that seated them. Resolves in the log. */
  readonly cites: EventId;
}

/**
 * One life the world has to offer somebody about to be born into it.
 *
 * Phase 6 decision 3 (*B-lite*) and
 * `docs/research/player-fantasies/fantasy-01-born-into-the-world.md` §2: the
 * start screen offers lives the world has already lived, the player picks one
 * and types a name, and the birth is real. Derived from the log on the call and
 * never stored, like every other projection here.
 *
 * **A card is chosen by its handles, never by its place in the list.** Read
 * {@link OriginCard.kind}, and hand the fields that door takes back to the
 * matching command — {@link WorldSource.queueBornOrphan} takes `settlement`,
 * {@link WorldSource.queueBornUnrecorded} takes `mother` and `father`,
 * {@link WorldSource.queueBornBound} takes `figure`. The other fields are what
 * the projection knows about the life anyway, for drawing the card.
 *
 * **Nothing on it is prose.** What a card *says* is the log's own sentence about
 * {@link OriginCard.cites} — the sack an orphan survived, the birth a child was
 * written down by — and the names are the world's own. A client that composed
 * "an orphan of Barfell, sacked in the year 168" out of parts would be authoring
 * the world's fiction in JavaScript (devlog #2).
 */
export interface OriginCard {
  /**
   * Which door: {@link ORIGIN_ORPHAN}, {@link ORIGIN_UNRECORDED} or
   * {@link ORIGIN_BOUND}.
   *
   * Checked against the simulation's own ids at startup, because this is what
   * the switch above turns on.
   */
  readonly kind: number;

  /**
   * The word the simulation labels that door with — *orphan*, *unrecorded*,
   * *bound*.
   *
   * A debug label rather than a heading: it is what a trace panel prints and
   * what the startup check compares. The card's readable line is the client's
   * own composition over the row's names ({@link WitnessSource.describe} is
   * the chronicler's debug register, never diegetic text — the trace view is
   * where those sentences belong, keyed on {@link OriginCard.cites}).
   */
  readonly kindName: string;

  /** The town the life is lived in — ruined for an orphan, home for the rest. */
  readonly settlement: SettlementId;

  /** What that town is called. */
  readonly settlementName: string;

  /**
   * The record behind the offer: the sack for an orphan, a birth for a child.
   * Resolves in the log.
   */
  readonly cites: EventId;

  /** The mother, or `null` for a life the world remembers no parents of. */
  readonly mother: FigureId | null;

  /** What she is called, or the empty string when there is no mother. */
  readonly motherName: string;

  /** The father, on the same terms. */
  readonly father: FigureId | null;

  /** What he is called, or the empty string. */
  readonly fatherName: string;

  /**
   * The person this card *already is*, and `null` on the two doors that make
   * somebody.
   *
   * Only {@link ORIGIN_BOUND} fills it: that door takes over a figure the world
   * made on its own and re-rolls nothing about them, which is what makes *"you
   * have been here all along"* true rather than dressed up.
   */
  readonly figure: FigureId | null;

  /** What they are called today — the name the birth replaces. */
  readonly figureName: string;

  /**
   * The line the life is born into, or `null` where there is **nothing to
   * inherit**.
   *
   * `null` is not a lookup that failed: the birth door coins a family name for a
   * life with no line behind it (decision 5, inherit-or-coin), so a card showing
   * no house is a card whose player will be the first of one.
   */
  readonly house: HouseId | null;

  /** What that line is called, or the empty string. */
  readonly houseName: string;

  /**
   * The seats this life's parents held, oldest first. At most
   * {@link MAX_CLAIMS}, and empty for most lives.
   */
  readonly claims: readonly OriginClaim[];
}

/**
 * The witnessing reads, as the client is allowed to see them.
 *
 * Every method is a *query*: nothing here queues a command. The commands are on
 * {@link WorldSource} and there are nine of them now rather than three — Phase 3
 * decision 10 said the player is not a sim entity and adds no verb, Phase 5
 * amends exactly one clause of that (`docs/plans/player-as-figure.md` §1) — the
 * player's **body** still never crosses, and their **deeds** now do — and Phase
 * 6's three birth doors are that same amendment one door earlier in the session:
 * what crosses is a name and a citation of a life the log already holds.
 *
 * So the reads below gained the other side of those deeds — who the player is
 * ({@link WitnessSource.playerFigure}), the books anybody has left
 * ({@link WitnessSource.chronicles}), the lives they could have been born into
 * ({@link WitnessSource.origins}) — and one thing with no method of its own:
 * every spoken line is now composed *to* the claimed player, so a record that
 * names them may be said in the second person. That lens is set once, sim-side,
 * and the client neither passes it nor can turn it off (`crates/sim-wasm`'s
 * `heard`).
 *
 * **Phase 6 adds a second lens and lets the client hold this one**
 * ({@link WitnessSource.setSalienceDecay}, plan decision O7). It is the one
 * member here that is not a query, and it is still not a verb: what it writes is
 * a property of the *reading* — how fast spoken memory fades — which is
 * unhashed, absent from the replay, and incapable of changing what the world
 * does. The sentence above survives it whole: no command is queued here, and the
 * player's body still never crosses.
 */
export interface WitnessSource {
  /** Today's date. Read at a day boundary, not per frame. */
  date(): WorldDateView;

  /**
   * How far through the day it is, as a fraction in `[0, 1)`, or `null` when
   * this source has no clock to answer with.
   *
   * **The simulation's own tick-within-day** (Phase 5 decision 8): the world's
   * clock at a finer grain than {@link WitnessSource.date}, exact from the first
   * frame and needing no day to have gone by. `witness/witness.ts` asks for this
   * first and falls back to measuring a whole day between two date changes,
   * which is what a source with no clock — the fixture — leaves it doing.
   *
   * **Zero is the day boundary**, which the client reads as first light: the
   * date turns at dawn, the sky is keyed to this number, and nothing else in the
   * client is allowed to have an opinion about what time it is
   * (`render/daylight.ts`).
   *
   * The one read on this interface that is asked per *frame* rather than per day,
   * and cheap enough for it: a remainder and a division, with no bulk behind it.
   * A consumer must not cache it — that is the difference between this and
   * {@link WitnessSource.date} — and must check the range rather than trust it,
   * because a seam that silently answered in ticks would light the world at noon
   * all night.
   */
  timeOfDay(): number | null;

  /**
   * The figure the player's deeds cite, or `null` when nobody has claimed one.
   *
   * **The whole of the player inside the simulation** (Phase 5 decision 1): a
   * handle in the log's social graph, put there by {@link WorldSource.claim} and
   * written to by nothing else. Every other question about the player is a
   * question about that figure, asked with the reads that already existed — what
   * they are called, what byname their deeds have earned them, which town they
   * are of, what the log holds about them.
   *
   * `null` in every session before the claim lands, which is also how a client
   * learns whether a claim it queued was accepted: a tick has no error channel,
   * so the honest test is to ask afterwards.
   */
  playerFigure(): FigureId | null;

  /**
   * The settlement the player is standing in, or `null` when they are standing
   * in none.
   *
   * **One answer to "which town am I in"** (Phase 7, item 6's rider 4, ruled
   * 2026-08-28). There were three: this, which resolved the *nearest* town at
   * any distance; a client radius around a centre (`IN_TOWN_TILES`, deleted);
   * and the simulation's own promotion slot ({@link WitnessSource.promoted}).
   * Three answers is a window in which two of them disagree, and every symptom
   * the item-6 investigation chased fired inside it — a market panel for a town
   * whose roster nobody was drawing, a share-word offer shouted at a hillside,
   * a body that belonged to one answer vanishing under the other.
   *
   * So it is the promotion slot, and nothing else: a town is *here* exactly
   * while the simulation is holding it up, which since item 6 is while the
   * player's interest square touches its ground plus a halo (`sim_core::local`).
   * That is a wider radius than a player would call "in town" — a town is alive
   * before it can be walked into, because the simulation has to be ahead of the
   * eye — and it is deliberately the same authority the roster, the market, the
   * board and the bodies all answer to, because one of those disagreeing with
   * another is the bug this deletes.
   *
   * `null` is a real answer and every consumer has a branch for it: out in the
   * country there is no market to read, no town board to walk up to, nobody to
   * hand a piece of news to, and no roster. It is also what a session reads
   * **before its first tick**, because promotion happens inside one.
   */
  here(): SettlementId | null;

  /** Every settlement the client may need a name or a position for. */
  settlements(): readonly SettlementView[];

  /** The trade legs, which are the paths news and refugees travel along. */
  routes(): readonly RouteLeg[];

  /**
   * The column where the road from one settlement to another leaves the first
   * one's streets, or `null` for a pair this world does not have.
   *
   * **Where a road meets a town, asked rather than guessed** (wave 1.5). The
   * road between two settlements is terrain now — gravel the simulation beds
   * from the mouth of the main street on the side that faces the other town,
   * which is the same street the rampart's gate is cut in — and where that mouth
   * is depends on the town plan's own radius and the width of its mains, which
   * are the simulation's numbers and not this client's.
   *
   * A source that cannot answer (the fixture, whose towns are hand-authored)
   * returns `null` and the caller falls back to its own bearing arithmetic.
   */
  roadMouth(from: SettlementId, to: SettlementId): { readonly x: number; readonly y: number } | null;

  /** The visited settlement's living named figures. */
  roster(): readonly RosterEntry[];

  /** A settlement's market, one row per commodity. */
  market(settlement: SettlementId): readonly MarketRow[];

  /** A settlement's rumour store, freshest first. */
  rumors(settlement: SettlementId): readonly RumorEntry[];

  /**
   * Everything the log has left standing on the ground around the player.
   *
   * **Near, and not *here*** — the one clause of item 6's rider 4 the owner
   * amended (2026-08-28). A grave, a cairn and a burned farmstead are the
   * recordless layer's own texture: they are beside a road whether or not the
   * town at either end of it is being held up, so this list, {@link
   * WitnessSource.refugeeGroups} and {@link WitnessSource.pulses} are the three
   * reads on this surface that keep answering when {@link WitnessSource.here} is
   * `null`. Everything else that is *about a settlement* goes quiet with it.
   *
   * How a source scopes it is its own business and is not a second answer to
   * *"which town is the player in"*: the live one asks around the nearest
   * settlement and says so in as many words (`world/wasm.ts`'s `nearestTown` —
   * private, and a scope rather than an authority).
   */
  features(): readonly AftermathFeature[];

  /**
   * The refugee groups currently on the roads.
   *
   * "Currently" is the client's word and the client's window: the simulation
   * records that a group fled a battle, where the road led and on what day
   * (`BattleFought`'s own fields), and never where anybody is standing —
   * embodiment is presentation (decision 8). So how long a column counts as
   * still walking is a number `world/wasm.ts` holds beside the walk speed
   * `witness/witness.ts` draws it at, and the two move together.
   *
   * Road texture, so it keeps answering when {@link WitnessSource.here} is
   * `null` — see {@link WitnessSource.features}.
   */
  refugeeGroups(): readonly RefugeeGroup[];

  /**
   * The trade pulses currently on the roads.
   *
   * "Currently" is the client's word and the client's window, exactly as it is
   * for {@link WitnessSource.refugeeGroups}: the simulation computes a turn's
   * pulses and says where they went and on what day, never where anybody is
   * standing (Phase 3 decision 8). How long a caravan counts as still walking
   * is a number the source holds; nothing draws the walk since the cart was
   * retired, and the window only scopes what the board and the director read.
   *
   * Road texture, so it keeps answering when {@link WitnessSource.here} is
   * `null` — see {@link WitnessSource.features}.
   */
  pulses(): readonly TradePulse[];

  /**
   * The beats the simulation says are witnessable here, freshest first.
   *
   * Phase 4 decision 1's projection. `windowDays` is how far ahead of today to
   * look, in days — a *number of days* rather than a pair of dates, because
   * subtracting one from today needs to know how long a year is and the client
   * deliberately does not ({@link WorldDateView}). The window reaches forwards:
   * a beat whose material has not surfaced yet is exactly what
   * {@link SpotlightBeat.earliest} exists to describe.
   *
   * **A horizon rather than a radius, and the past is not lost by it.** Wave 1B
   * wrote "either side of today" against a fixture that ignores the window
   * entirely; wave 2A found the live projection has no need of the backwards
   * half. Material that *stands* — a grave, a ruin, the price currently on the
   * board, a rumour the town already holds — never stops being on offer, so it
   * comes back from a window that starts today however long ago it landed. What
   * a backwards window would add is only material that has been and gone: last
   * month's caravan, a column that arrived three weeks ago. Staging those would
   * be staging a reaction to something that is no longer there to meet.
   *
   * Pulled on a day boundary like every other bulk list (decision 11).
   */
  beats(settlement: SettlementId, windowDays: number): readonly SpotlightBeat[];

  /**
   * Asks a figure for a line of news, or `null` when they have none.
   *
   * Pulled on an interaction and never per tick. Which rumour the figure
   * reaches for is the simulation's choice, not the client's: the client asks
   * "say something" and gets back a sentence plus the record it is about.
   */
  speak(figure: FigureId): SpokenLine | null;

  /**
   * Asks a figure about one particular record, or `null` when they cannot speak
   * to it.
   *
   * The same projection as {@link WitnessSource.speak} with the record named
   * instead of chosen — which is what a crier is. Phase 4 decision 5 stages the
   * town's reaction to a battle landing by having the nearest figures say **the
   * record's line**, and the alternative would be the client composing a
   * sentence about a record, which is the one thing above this seam that is
   * never allowed (devlog #2, decision 9).
   *
   * `null` rather than a substitute when the figure's settlement has never
   * heard of the record: a speaker who says something *else* would put the
   * wrong `EventId` in the journal, which is worse than silence.
   */
  speakOf(figure: FigureId, event: EventId): SpokenLine | null;

  /**
   * Asks whoever is walking with a caravan for a line, or `null`.
   *
   * No client caller since the drawn cart was retired; the seam stays because a
   * column on the road will ask the same question of the same origin.
   *
   * A separate method from {@link WitnessSource.speak} for the reason
   * {@link WitnessSource.speakGroup} is: the talker belongs to the *origin*
   * settlement, so the line is composed against that settlement's rumour store
   * and that figure's own hops — which the visited settlement's roster cannot
   * answer for (Phase 4 decision 2: "their own store's freshest news at their
   * own hops").
   */
  speakPulse(pulse: number): SpokenLine | null;

  /**
   * Asks a figure who they are.
   *
   * **A figure with no news must not be silent** (Phase 4 decision 10b): the
   * roster is the town's living people and most of them, most of the time, have
   * nothing the player has not already heard — {@link WitnessSource.speak}
   * answers `null` for them, and a talk verb that produced nothing at all is a
   * verb a player stops pressing. This is `speech::introduce`: a projection over
   * the figure's *own* record — their name, the byname their own records earned
   * them, where they are from, and one facet-coloured clause.
   *
   * A string rather than a {@link SpokenLine}, because there is no record to
   * cite: an introduction is about the speaker and puts nothing in the journal.
   * That is also why it is a separate method rather than a fallback inside
   * `speak` — "they had nothing to tell me, so they told me who they were" is a
   * different thing to show, and which one to show is presentation.
   *
   * The empty string for a figure this world does not have.
   */
  introduce(figure: FigureId): string;

  /**
   * Asks what a figure's own records have earned them to be called, or `""`.
   *
   * The epithet — *"the Twice-Besieged"* — projected out of that figure's own
   * deeds and filtered and sorted like every other viewer's answer
   * (`sim_core::history::byname`, `history/epithet.rs`'s fifteen phrases), read
   * across the boundary through `WasmSim::figure_byname`.
   *
   * **`""` is the ordinary answer, not a failure.** An epithet is earned by a
   * record, most figures never earn one, and most of those never will — so a
   * surface over this read has to say the empty case honestly rather than fill
   * it (`docs/decisions/ui.md` §4.8, the you page's *known for*).
   *
   * A separate method from {@link WitnessSource.introduce} because that sentence
   * is written over `Figure::name` alone and does not carry the byname, so a
   * client that wanted the epithet could not see one at all. It is **the sim's
   * word**: what crosses is `sim-core`'s own string, shown verbatim or not
   * shown — the client never composes a byname and has no second way to one.
   */
  byname(figure: FigureId): string;

  /**
   * Asks a refugee group what they are running from, or `null` if they will not
   * say.
   *
   * The same projection as {@link WitnessSource.speak} over the records in
   * {@link RefugeeGroup.refs}. It is a separate method because a group is not a
   * figure and has no facets (history §8.4 — populations, not people), so the
   * delivery is coloured by the flight rather than by a personality. The client
   * must not compose this line itself: authoring text about a record in
   * JavaScript is the exact failure devlog #2 names.
   */
  speakGroup(group: number): SpokenLine | null;

  /**
   * The books standing in a settlement, oldest first.
   *
   * Phase 5 decision 5's read side. Asked per settlement rather than of the
   * world because a book belongs to a town by construction — one left further
   * out than a book's reach is not left at all — and because it is a read
   * *about where the player is*, like the roster and the market.
   *
   * A row says where a book is and who wrote it; {@link ChronicleView} says why
   * there is no text on it.
   */
  chronicles(settlement: SettlementId): readonly ChronicleView[];

  /**
   * What one book says, one line per record it cites.
   *
   * The **written** register over the book's own citations — no contractions,
   * the year spelled out, the ground under it the town the book stands in. The
   * same voice a market board posts in ({@link WitnessSource.notice}) and
   * deliberately not a mouth's: a page is read, not heard.
   *
   * Composed by `sim-core` and crossing whole, exactly as a spoken line and an
   * inscription do. The client puts these on a page and composes nothing.
   *
   * `chronicleLines(b)[i]` voices `chronicleEntries(b)[i]`: same order, same
   * length.
   *
   * Empty for a book this world does not have.
   */
  chronicleLines(chronicle: number): readonly string[];

  /**
   * The records one book cites, in the order it cites them.
   *
   * **The half of a book the journal keeps.** A journal entry is a reference and
   * its provenance, never a copy of what was said (Phase 3 decision 2), so
   * reading a chronicle has to yield ids as well as sentences: the line is what
   * the player read and the {@link EventId} is what they now hold — resolvable
   * in the trace view, and the same id a price, a grave or a mouth may be
   * carrying.
   *
   * That is what makes a book a witness channel rather than decoration, and it
   * is what the simulation already treats one as: a settlement's own shelves
   * teach it these same ids, one a month. A client filing them is doing what the
   * town does.
   *
   * Parallel to {@link WitnessSource.chronicleLines} — same order, same length,
   * so `entries[i]` is the record `lines[i]` voices. Two methods rather than one
   * pairing because the two halves sit on opposite sides of decision 11's split
   * (numeric bulk flat, strings per call), and a caller that only wants to
   * *file* a book it has already read should not re-render every page to do it.
   *
   * Empty for a book this world does not have.
   */
  chronicleEntries(chronicle: number): readonly EventId[];

  /**
   * The towns still in ruin, and the battle each one is drawing.
   *
   * Phase 5 decision 11. **A town that is not in one has no row** — the list is
   * short in a world at peace and empty in one that has never been at war, which
   * is the answer rather than an omission. A client reads it into a set on a day
   * boundary and asks that set about the town it is standing in.
   *
   * The whole world rather than one settlement, because it is the same list from
   * everywhere: a map that marks ruined towns wants all of them, and there are
   * never many.
   */
  ruins(): readonly RuinView[];

  /**
   * The world's own history, as the Book of Eras keeps it — oldest age first.
   *
   * Phase 6 decisions 2, O5 and O6. The whole log cut into ages, each remembered
   * for the records it turned on, with **every sentence paired to the records it
   * narrates** ({@link SagaLine.cites}) so a trace view can put a finger on each
   * one. That pairing is the contract: a page of prose the log cannot account
   * for is the one thing the saga is not allowed to be.
   *
   * The whole book in one call, because a book is what somebody opened: it is
   * read at a shelf or on a start screen, once, and never on a frame or a day
   * boundary. Nothing here is stored — the projection is derived on the call, so
   * a build that words a chapter differently words a *chapter* differently and
   * no replay can tell.
   *
   * Empty for a world with no past, which is `?bootstrap=0` — the same answer
   * this surface already gives everywhere else for a world where nothing has
   * happened. **One age is not an empty answer**: a quiet seed gets one,
   * honestly.
   */
  saga(): readonly SagaEra[];

  /**
   * The lives a player could be born into, in the order the screen shows them.
   *
   * Phase 6 decision 3's projection — three doors, each carrying the record
   * behind it, the people it names, the line it is born into and the seats those
   * parents held ({@link OriginCard}). Read once, on the start screen, before
   * play: it walks the whole log and is derived on the call, like the saga, and
   * nothing above this seam caches it.
   *
   * **Empty is a real answer** and a young world's ordinary one. A session that
   * finds it falls back to {@link WorldSource.claim}, which needs only a town.
   *
   * **It does not empty once somebody is born.** The screen is the shape rules
   * over the log and knows nothing about who is playing; what makes a second
   * birth impossible is the door refusing it. A flow that shows the screen twice
   * is a flow with a bug, not a source with one.
   */
  origins(): readonly OriginCard[];

  /**
   * The seats a figure's parents held, oldest first.
   *
   * {@link OriginCard.claims} asked about somebody who already exists — the
   * player after the birth, when the card is gone and the figure is what is left
   * — and equally about anybody at all, because a claim is a fact about
   * parentage and the projection has no idea who is playing. That is the parity
   * half of it: the same read answers about a ruler's son nobody is playing.
   *
   * At most {@link MAX_CLAIMS}. Empty for a figure the world remembers no parent
   * of — every founder, every champion from outside — and for a player who came
   * through {@link WorldSource.claim} rather than the birth door.
   */
  claims(figure: FigureId): readonly OriginClaim[];

  /**
   * Sets how fast this session's reading says spoken memory fades, in **days**,
   * or turns it off with `null`.
   *
   * Phase 6 decisions 7 and O7's knob. The only method on this interface that is
   * neither a command nor a query, and it is allowed to exist because of what it
   * writes: a **lens**, which is a property of the reading rather than of the
   * world. Nothing about it is queued, recorded or hashed — two sessions of one
   * command log at different settings agree on every hash and disagree about how
   * well anybody remembers — so it is safe to hang off a URL parameter in a
   * production build, which is what O7 requires (the owner playtests the
   * deployed preview; a knob behind `import.meta.env.DEV` is a knob nobody
   * judges).
   *
   * **What it changes**: the mouths. A spoken line loses a step of detail per
   * half-life on top of the mouths and the years it already loses it for
   * ({@link WitnessSource.speak}, {@link WitnessSource.speakOf},
   * {@link WitnessSource.speakPulse}, {@link WitnessSource.speakGroup}), and the
   * square stops offering word beats about tellings it heard several half-lives
   * ago ({@link WitnessSource.beats}).
   *
   * **What it deliberately does not**: anything written down. A grave, a market
   * board, a book on a shelf and {@link WitnessSource.saga} say the same thing
   * at every setting, because *"forgetting is designed … making
   * chronicles/stones the only permanent memory"* is the point of the rider and
   * not a gap in it.
   *
   * `0` is passed through rather than read as off — the simulation has ruled
   * that it means *everything is forgotten* — so **`null` is how a caller turns
   * the knob off**, and so is any negative or non-finite number.
   *
   * A source whose lines are authored rather than projected (`world/fixture.ts`)
   * takes the call and has nothing to fade; there is no getter, because the
   * caller is the only thing that ever set it.
   */
  setSalienceDecay(days: number | null): void;

  /**
   * What is carved on a feature, or the empty string when nothing is.
   *
   * The same idea as a spoken line, in stone (items §10.4's slab is a figure
   * reference, not authored text).
   */
  inscription(feature: number): string;

  /**
   * The log's own sentence for a record — `HistoryEvent::narrate`.
   *
   * The debug half of the traceability the phase exists to prove: a journal
   * entry holds an {@link EventId}, and this is how a debug view turns that id
   * back into the line the chronicler would write. Never shown as diegetic
   * text; the diegetic text is {@link WitnessSource.speak}'s.
   *
   * **Debug surfaces only**, and it took a wave of the voice overhaul to make
   * that true: the market board's cause line used to be this string, so a player
   * reading a board met `settlement #12` and a casualty subtraction. The board
   * asks {@link WitnessSource.notice} now.
   */
  describe(event: EventId): string;

  /**
   * What a market board posts about the record that moved a price —
   * `speech::notice`.
   *
   * {@link WitnessSource.describe}'s diegetic opposite, and the reason it has
   * one. A market row carries the shock's `cause` ({@link MarketShock.cause}),
   * and the board explaining *why* the price moved is what makes the market a
   * channel rather than a gauge (plan decision 7). This is that record in the
   * world's own register: written, dated by year, nobody's mouth.
   *
   * `settlement` is the town the board stands in, and it is the same handle the
   * caller passed {@link WitnessSource.market} to get the row this cause came
   * off. **A board speaks with the ground under it**: a record about that town
   * posts "this town was stormed in year 169", and the next town's board keeps
   * naming the place. So the *pair* is the identity — a client must not cache a
   * notice by the record alone, because the same `EventId` is two sentences in
   * two towns.
   *
   * Composed by `sim-core` and crossing whole, exactly as a spoken line and an
   * inscription do — a client that assembled a notice out of a place and a year
   * would be authoring the world's fiction (devlog #2). The empty string for a
   * record or a settlement this world does not have.
   */
  notice(settlement: SettlementId, event: EventId): string;

  /*
   * The place the player is standing in (Phase 7, wave 1F).
   *
   * A third cadence and a third scale. Everything above is pulled on a **day
   * boundary**, because the abstract tier only moves on one; the reads below are
   * the *local tier*, which moves on a job pulse — a fraction of an in-game hour
   * (`sim_core`'s marketplace) — so they are pulled on the client's own scene
   * cadence and never per frame.
   *
   * What they have in common is that every one of them is empty in a world
   * nobody is watching. The local tier exists only while a settlement is
   * promoted, which happens when the interest command reaches its ground
   * ({@link WitnessSource.promoted}), so a session out in the country reads
   * exactly what a headless run reads: nothing.
   *
   * # The one contract, written down once
   *
   * **The host reports interest when it changes; the simulation holds it;
   * coverage is evaluated every tick from what is held** (item 6, ruled
   * 2026-08-28). Both halves of this boundary used to be right about their own
   * side and wrong about each other's: the simulation's counter assumed an
   * interest command arrived every tick, and the client deduped it to chunk
   * crossings on the belief that a repeat changes nothing. A repeat changes
   * nothing — that is now true rather than nearly true — because the held set is
   * sim state and a second copy of it is not news
   * (`world/wasm.ts`'s `submitInterest`). What ends a promotion is a *new*
   * interest that no longer covers the town, never silence.
   *
   * # The one thing that changed about decision 8
   *
   * *"The simulation never learns where an NPC is standing"* held for four
   * phases and wave 1F amended it for exactly one settlement: a promoted town's
   * roster is put on real ground and walks real paths sim-side
   * ({@link WitnessSource.presentFigures}). Item 6 finished the sentence —
   * **one owner per body**: a named figure is drawn from that list or is not
   * drawn at all, and the client's ambient loop, which used to stand the same
   * roster up a second way in the same street, draws no figures at all now. The
   * things still embodied client-side are the ones the simulation holds no
   * position for and never will: a column of refugees, and (from Phase 8) the
   * unnamed crowd a city's population is made of. A caravan on a road was one of
   * them until its cart was retired; it comes back as a column the sim places.
   */

  /**
   * The settlement the world is standing in, or `null` when nothing is
   * promoted.
   *
   * **World state, not arithmetic**, and since item 6 it is also
   * {@link WitnessSource.here}'s whole answer: a settlement promotes when
   * {@link WorldSource.setInterest} covers its ground and its halo, so the
   * answer is hashed and the same for two hosts replaying one command log.
   * Everything below is about this town and no other.
   */
  promoted(): SettlementId | null;

  /** The work the promoted town wants done, in board order. Empty when nothing is promoted. */
  jobs(): readonly JobPost[];

  /** Every body the promoted town has on the ground, in handle order. */
  presentFigures(): readonly PresentFigure[];

  /**
   * Whether a figure has come of age and is alive.
   *
   * **Life stage is derived, never stored** (item 6's rider 2, ruled
   * 2026-08-28): it is a pure function of when a figure was born, what the
   * calendar says now, and the race's own stage table, so nobody "ages up" in
   * code and there is nothing here for a client to cache. Asking is the whole
   * interface — a client that took a birth date and a year and worked it out
   * would be keeping a copy of a stage table that is 2A's body-plan **data**,
   * per race, and that is the thing above this seam that is never allowed
   * (devlog #2).
   *
   * It exists because presence stopped being an adults-only roster: children get
   * bodies or they do not exist on the street, so `residents` hands out one to
   * every living resident and a client that says *"ten alive here"* had to be
   * able to say how many of them are grown.
   *
   * **Nothing in this client reads it since wave 2A**, and it is kept rather
   * than deleted because it is a *different question* from
   * {@link WitnessSource.lifeStage} beside it. This one is the abstract tier's
   * — the stored coming-of-age flag its actor pools, its seats and its
   * successions are drawn from (`sim_core`'s `Figure::is_actor`) — and that one
   * is the body's, derived from a birth date and the race's stage table. The
   * two are pinned to agree at every day boundary sim-side, and the surfaces
   * that will want *this* one are the ones about eligibility rather than about
   * size: a played heir at the origins door, and whatever 2D makes of it.
   *
   * `false` for a figure this world has never had, and for one who is dead —
   * which are the same answer for the same reason every other read here gives
   * it, and the reason a caller must not read `false` as *"a child"* without
   * knowing the handle is on a roster.
   *
   * One figure per call, on {@link WitnessSource.itemLabel}'s terms: it is asked
   * over a roster of a dozen at the cadence a debug window refreshes at, and a
   * flattened *"who here is grown"* array would be a second layout to keep in
   * step with the roster's.
   */
  isAdult(figure: FigureId): boolean;

  /** What the player is carrying, in handle order. Empty before a claim. */
  carried(): readonly CarriedItem[];

  /**
   * What the player is **wearing**, in handle order (Phase 7, wave 2D-b).
   *
   * {@link WitnessSource.carried}'s twin one union arm over, and the same rows,
   * so a panel draws the hands and the body through one decoder. *Which part*
   * each piece is on is deliberately not here: a client that drew *"on the
   * head"* would be keeping a copy of the armour table and of the body plan, and
   * what it draws instead is the simulation's own label for the thing.
   *
   * Empty for almost every session forever — nothing in the economy hands
   * anybody a breastplate, so a player has to make one.
   */
  worn(): readonly CarriedItem[];

  /**
   * What the player could shoot with: the bow in their hands and how many
   * arrows are left.
   *
   * **One read because the two refusals are different sentences.** *You have no
   * bow* and *you have no arrows* are two things to tell a player, and a client
   * given only *"you cannot shoot"* would have to guess which. The bow is a
   * handle rather than a flag so a surface can name it through
   * {@link WitnessSource.itemLabel} — the simulation owns the words.
   *
   * `{ bow: null, arrows: 0 }` before a claim and for the overwhelming majority
   * of players, who are carrying a boulder.
   */
  quiver(): { readonly bow: number | null; readonly arrows: number };

  /**
   * The piece {@link WorldSource.wear} would put on next, or `null` for a press
   * that would do nothing.
   *
   * **The simulation's own pick, asked rather than guessed.** A client counting
   * armour in the hands would get it wrong the moment somebody carried a second
   * breastplate: what decides is whether the body *part* is bare, and the part
   * table is the simulation's (`sim_core::hands`). A handle rather than a
   * boolean, so a prompt can name the thing through
   * {@link WitnessSource.itemLabel}.
   */
  wearable(): number | null;

  /**
   * What the player has chosen to hold in front, or `null` for hands nobody has
   * arranged.
   *
   * **{@link WorldSource.wield}'s read side, and the difference it answers is
   * why it is its own read**: `carried()[0]` is *whatever happens to be first*
   * and this is *what they chose*. Only the second one may light a slot — a
   * surface that lit the head of the carry list would light the boulder somebody
   * picked up a minute ago.
   *
   * **It is not *the weapon you would swing with***, and that gap is filed
   * rather than papered over (`docs/decisions/player.md` §24.8):
   * `combat::weapon_of` is private, so what a surface may honestly draw is the
   * thing in the hand and nothing more. Wielding a boulder is legal and means
   * the weapon pick walks past it.
   *
   * A handle rather than a flag, on {@link WitnessSource.quiver}'s terms: the
   * word for the thing comes back through {@link WitnessSource.itemLabel},
   * because the simulation owns the words.
   *
   * `null` in every session before the door is pressed, which is nearly all of
   * them.
   */
  wielded(): number | null;

  /**
   * The world's first words to the player, for one life on the birth screen.
   *
   * **The waking sentence** (chunk S-1, `docs/decisions/speech.md`): one
   * simulation-composed sentence for the moment the veil lifts, drawn from what
   * the records know of the chosen life. It is written prose from the board's
   * register — *written, and nobody's mouth* — and it renders verbatim like
   * every other sentence that crosses here. The client had no business composing
   * it and this is the read that means it never has to (charter rule 2).
   *
   * `origin` is the **row index** into {@link WitnessSource.origins}, exactly as
   * {@link OriginCard.claims} is a question about the list that produced it; one
   * past the end is the empty string, which is this boundary's convention for a
   * handle that names nothing.
   *
   * `name` is what the player typed, and **the empty string is the nameless form
   * and the default** — an unnamed session never triggers a name-dependent beat
   * (the owner's standing ruling). It goes through the simulation's own
   * `PlayerName` on the way in, so the sentence carries the name the record will
   * carry rather than a second normalisation of it.
   */
  wakingLine(origin: number, name: string): string;

  /** What is lying on a tile, in handle order. Empty for almost every tile. */
  tileItems(pos: TilePos): readonly CarriedItem[];

  /**
   * What one item is called — *"masterwork oak table"*, *"granite boulder"*.
   *
   * **Three tables the simulation owns, composed sim-side and crossing whole**:
   * the rung, the material and the noun. A client that assembled the phrase out
   * of the three ids on the row would be keeping copies of three vocabularies,
   * one of which is the raws — which is the thing above this seam that is never
   * allowed (devlog #2).
   *
   * The handle is resolved against the arena **as it is now**. Items are the one
   * family of handle this simulation removes, so a number filed away a minute
   * ago may name nothing or name something else, and the empty string is the
   * honest answer to both.
   */
  itemLabel(item: number): string;

  /**
   * What the world *calls* one item — *"Karfang"* — or the empty string for
   * anything it has no name for, which is nearly everything.
   *
   * **The promoted tier's read** (`sim_core::Snapshot::item_name`, Phase 7's
   * decision 3): a thing gets a name when something record-worthy touches it —
   * a killing, a masterwork — and until then it is pure data with no history.
   *
   * A second call beside {@link WitnessSource.itemLabel} rather than a widening
   * of it, and the split is the one decision 11 draws everywhere: the label is
   * what a thing *is*, identical for every iron blade in the town, and this is
   * what one particular thing is *called*. The comma between them is the
   * client's ({@link Witnessing}'s `itemWords`), which is composition rather
   * than a fourth vocabulary crossing the wall.
   *
   * Unlike the label, this cannot go stale: a named item is never removed
   * (`sim_core::Items::remove` refuses one), so a number that resolved to a name
   * a minute ago resolves to the same name now.
   */
  itemName(item: number): string;

  /**
   * What the world remembers about one named thing, one line each. Empty for
   * anything that is not one.
   *
   * **The item-history projection** (`sim_core::relics`): who made it, when it
   * was named and what for, every killing it is cited on, and where the world
   * last *heard* of it — which is not where it is. The drift between the two is
   * items §10.4's rumour mechanic and not a bug to close: the log knows the town
   * a famous blade was last seen in, and the arena knows whose hand it is in.
   *
   * The handles are already resolved into names on the far side, exactly as a
   * journal line's are.
   */
  relicLines(item: number): readonly string[];

  /**
   * Every named thing in the world, in handle order.
   *
   * **The registry as a list** (`sim_core::Snapshot::relics`), in the arena's
   * own order — which is the order the world hash folds in, so two hosts list
   * the world's named things identically. Short by construction: promotion is
   * by named trigger and the triggers are rare.
   *
   * The rows are {@link WitnessSource.carried}'s and {@link
   * WitnessSource.tileItems}' layout, so one decoder reads all three; what each
   * one is *called* is {@link WitnessSource.itemName}, per item, like every
   * other string.
   */
  relics(): readonly CarriedItem[];

  /**
   * Whether a kind of item is one a pair of hands lifts.
   *
   * **The pickup verb's gate, and it is the simulation's table** — a door, a
   * table and a library's shelf stay where they were put (`sim_core::ItemKind`'s
   * `is_portable`, which `sim_core::hands` reads to pick the first *portable*
   * handle on a tile). The same bargain {@link TownLot.trade} makes for the
   * workshop verb and {@link WitnessSource.workAt} makes for the gathering one:
   * the judgement is the simulation's and the sentence is the client's.
   *
   * It exists because the offer is drawn here and the refusal is not drawn at
   * all. Every furnished workshop has a table on its work tile and every
   * standing building has a door (`sim_core::townplan`'s furnish pass), so a
   * client that offered *"pick up"* over whatever was lying on a tile put
   * `F — pick up brick table` over a smithy's bench and then sent a command the
   * simulation dropped in silence — the owner's playtest of 2026-08-30, and a
   * verb that looks broken.
   *
   * **A client that transcribed the rows instead would be keeping a copy of a
   * vocabulary the simulation owns**, which is the thing above this seam that is
   * never allowed (devlog #2) — and worse here than for a word, because this one
   * decides whether a command goes out at all.
   *
   * Asked per **kind** rather than per item, on
   * {@link WitnessSource.lotName}'s terms: it is a fact about a row of a table
   * rather than about a thing in the world, there are ten answers, and none of
   * them moves while a session runs. `false` for an id this build does not ship,
   * which is the safe direction as well as the honest one.
   */
  itemPortable(kind: number): boolean;

  /*
   * The body plan's four rows, wave 2A (`sim_core::body`).
   *
   * All four are asked per **stage** rather than per body, on
   * {@link WitnessSource.itemPortable}'s terms: they are facts about rows of a
   * table rather than about a person in the street, there are four answers, and
   * none of them moves while a session runs. Which stage a given body is in
   * rides its own row ({@link PresentFigure.stage}).
   *
   * They exist because a body's size, pace and name belong to the body plan and
   * the body plan is data. Until wave 2A the client authored two scale numbers
   * and the word `(child)` itself — a stub the owner ruled on 2026-08-29 with
   * this as the named replacement — and a client that authored them again would
   * be keeping a copy of a table it does not own (devlog #2).
   */

  /**
   * How tall a body at a life stage is drawn, in parts per thousand of a grown
   * one.
   *
   * Per mille rather than a fraction because the simulation's numbers are
   * integers, and it is the cube root of the ratio of body volumes taken
   * sim-side — so no renderer takes a cube root of a person. A thousand for a
   * stage id this build does not ship, which draws an unknown body grown rather
   * than flat.
   */
  bodyScale(stage: number): number;

  /**
   * How many ticks a body at a life stage spends crossing one tile.
   *
   * The gait table's row ({@link WorldSource.walkTicksPerStep} is the grown
   * one), so a crowd of mixed ages is smoothed at mixed speeds without the
   * client knowing why any of them is what it is.
   */
  walkTicksForStage(stage: number): number;

  /**
   * What a plate calls a body at a life stage, or `""` for a grown one.
   *
   * **The empty string is an answer**: an adult is unmarked, a name on its own.
   * The three young stages are named out of the simulation's own vocabulary,
   * which is what deletes the client-side `(child)` tag.
   */
  lifeStageWord(stage: number): string;

  /**
   * Whether a body at a life stage is small enough to be picked up.
   *
   * {@link WitnessSource.itemPortable}'s twin for people, and it exists for the
   * same reason: the **offer** is drawn on this side of the wall and the
   * refusal is not drawn at all, so a client that offered *pick up* over
   * everybody would put the verb over an adult and watch the command come to
   * nothing. It is not a baby rule — the answer comes off the size curve.
   */
  lifeStagePortable(stage: number): boolean;

  /**
   * Whether a body at a life stage swings at anybody.
   *
   * {@link WitnessSource.lifeStagePortable}'s sibling one verb over, and it
   * exists for the same reason: the **offer** is drawn on this side of the wall
   * and the refusal is not drawn at all, so a client that put a strike prompt
   * over everybody would offer a nine-year-old a swing the simulation then
   * refuses in silence.
   *
   * **It is the same row an NPC child is refused by** — rider 2's ruling 1,
   * *stage capabilities apply to the player exactly as to NPCs* — which is what
   * makes *a child cannot fight* one table asked from both sides rather than a
   * rule written twice.
   */
  lifeStageFights(stage: number): boolean;

  /**
   * Which life stage a figure is in, for a figure that may have no body.
   *
   * {@link PresentFigure.stage}'s counterpart for a *roster* line rather than a
   * drawn body: the debug window splits *"ten alive here"* by stage and a roster
   * holds people the local tier has not embodied. `-1` for a figure this world
   * has never had.
   *
   * One figure at a time, and deliberately: it is asked once a second over a
   * roster of a dozen while a window is open, and never on a per-frame path.
   */
  lifeStage(figure: FigureId): number;

  /**
   * How hurt a figure is, as `sim_core::health::Condition`'s own id.
   *
   * **A class and never a number.** The simulation keeps three accumulators per
   * tissue layer of every wound and hands a client one of four ids —
   * `combat-wounds-body.md` §6.1b's *model richly, summarise brutally* — because
   * a damage number computed on this side of the wall is a bug by definition
   * (Phase 7's cross-cutting rule). `0` is unhurt, which is almost everybody
   * forever and is also the answer for a figure this world has never had.
   *
   * {@link WitnessSource.lifeStage}'s cadence exactly: once a second over a
   * roster of a dozen while a window is open, never on a per-frame path.
   */
  condition(figure: FigureId): number;

  /**
   * What somebody would call a condition, or `""` for a body with nothing wrong
   * with it.
   *
   * {@link WitnessSource.lifeStageWord}'s twin, and the empty string is an
   * answer for the same reason: a whole body is unmarked. The words are the
   * simulation's, so a client that authored its own would be keeping a copy of a
   * table it does not own (devlog #2).
   */
  conditionWord(condition: number): string;

  /**
   * Whose hand a figure is running from, or `null` if they are not running.
   *
   * Derived sim-side out of the wound they are carrying and their own bravery
   * (`sim_core`'s `combat`), so the row that says *fleeing* and the body walking
   * away are one answer rather than two. `null` for everybody in an ordinary
   * town.
   */
  fleeingFrom(figure: FigureId): FigureId | null;

  /**
   * Who a figure is taken up with, or `null` if nobody.
   *
   * **The engagement floor** (`sim_core`'s `local`, the phase plan's rider 3):
   * while this answers, neither body is walking and neither is being sent
   * anywhere, so the two of them standing together is a fact rather than a
   * coincidence. Today the only thing that engages anybody is a watchman making
   * an arrest; when the conversation area lands, a talk is the same state read
   * through the same call.
   */
  engagedWith(figure: FigureId): FigureId | null;

  /**
   * Whom a figure is about to swing at, or `null` if nobody.
   *
   * {@link WitnessSource.fleeingFrom}'s twin, and one derivation split by one
   * dial sim-side (`sim_core`'s `combat`): a body carrying a fresh wound from
   * somebody still standing here either answers or runs, and its own bravery
   * says which.
   *
   * **This is what tells the client when to start speaking** (Phase 7 plan,
   * W11). The simulation knows where the player is standing only because this
   * client says so ({@link WorldSource.stand}), and the honest moment to start
   * saying it is the pulse before somebody swings. Watched beside
   * {@link WitnessSource.engagedWith}, the pair covers both ways a body ends up
   * striking the player: answering a blow, and a watchman's arrest.
   *
   * `null` for everybody in an ordinary town.
   */
  answering(figure: FigureId): FigureId | null;

  /**
   * Whom a figure follows, or `null` if they follow nobody.
   *
   * **The companionship, read** (`sim_core`'s `companions`, decision 10). A
   * handle rather than a word, so this client compares it against its own player
   * figure and never learns a rule: *following you* and *following Halvard
   * Reedmere* are the same read with a different answer.
   *
   * It is also **the second thing that makes the client keep saying where the
   * player is standing** (W11, as wave 2F widened it): a companion walks after
   * the tile the host states, because the player has no body for the simulation
   * to walk after. A session that stopped saying it would be a session whose
   * companions stood still.
   *
   * `null` for everybody in an ordinary town.
   */
  leaderOf(figure: FigureId): FigureId | null;

  /**
   * Everybody who follows a figure.
   *
   * {@link WitnessSource.leaderOf}'s other direction, bounded sim-side by
   * {@link MAX_COMPANIONS} — so a client may ask it about its own figure every
   * frame the way it asks what is in its hands. Empty for everybody nobody
   * follows.
   */
  companions(figure: FigureId): readonly FigureId[];

  /**
   * What a figure would say if they offered to follow the player, or `""` if
   * they would not.
   *
   * **One read rather than a flag and a line**, and that is the point: a client
   * that could draw the prompt without the sentence would be a client with an
   * opinion about who offers. The rule is the simulation's — engagement, two
   * dials, a life stage, and what this figure's town has heard of the player's
   * deeds — and so are the words, assembled over a record the town has actually
   * heard (`sim_core`'s `companions` and `speech`).
   *
   * It asks **no distance**. Where the player is standing is a sentence this
   * client says, and a read that needed one would have it streaming its position
   * all session to find out whether to draw a prompt; the reach is checked at
   * the press instead ({@link withinBeckonReach}, and the simulation's own copy
   * of it at {@link WorldSource.beckon}).
   *
   * `""` for nearly everybody in nearly every town.
   */
  followOffer(figure: FigureId): string;

  /**
   * Whether a figure would follow the player at all.
   *
   * The rule without the sentence, for the surface that is decided every frame:
   * an interact prompt is a verb and a line is a string every time it is asked
   * for.
   */
  offersToFollow(figure: FigureId): boolean;

  /**
   * What a follower says when they will come no further, or `""` while they are
   * still coming.
   *
   * **The halo, spoken** (`sim_core`'s `companions`): v1 companions follow
   * within promoted ground, and the predicate behind this is the town's own
   * coverage test — so a companion comes exactly as far as the simulated ground
   * does and this client says so rather than deciding it.
   *
   * `""` for everybody who follows nobody and for every follower whose hero is
   * still on the town's ground, which is the ordinary case and is why this may
   * be polled.
   */
  partingLine(figure: FigureId): string;

  /**
   * The tile the simulation is holding as the player's, or `null` if it holds
   * none.
   *
   * The read-back half of {@link WorldSource.stand}, and it exists so a client
   * can tell *"the world has not heard from me"* from *"the world has last
   * tick's tile"* — one refusal apart at {@link WorldSource.strike}, and a
   * prompt that could not tell them apart would be guessing which of its own
   * sentences was true.
   *
   * `null` in every session nobody has ever been engaged in, which is nearly all
   * of them. **It is not a position**: nothing here gives the player a body.
   */
  stood(): TilePos | null;

  /**
   * What is wrong with a body, as the {@link AFFLICTED_SENSELESS} bit mask.
   *
   * {@link WitnessSource.condition}'s companion, and the split is who is asking:
   * a condition is the one word a stranger reads off somebody walking towards
   * them, and these are the facts a person has about their **own** body —
   * senseless, off their feet, empty-handed, going faint.
   *
   * Every bit is a verdict the simulation reached. What this client does with
   * them is presentation and nothing else: a screen that goes dark, a gait that
   * slows, a word at the edge of the frame.
   *
   * Zero for everybody nothing has happened to. Asked about **one** figure —
   * the player's — a few times a second, never over a roster.
   */
  afflictions(figure: FigureId): number;

  /**
   * The record that says a figure died, or `null` while they live.
   *
   * **The read permadeath is built on** (plan decision 7). A client watching its
   * own figure learns that it is over from the *log*, and what it shows next is
   * the log's own words: hand this id to {@link WitnessSource.notice} and the
   * killer, the weapon and the cause come back as a sentence the simulation
   * composed. Nothing about how somebody died is written here.
   *
   * `null` for a living figure and for a handle this world has never had, which
   * are the same answer for the same reason: there is nothing written down.
   */
  deathOf(figure: FigureId): EventId | null;

  /**
   * The record of the last time the watch took a figure, or `null`.
   *
   * {@link WitnessSource.deathOf}'s twin one record over (chunk Fx-5), read for
   * the same reason and used the same way: hand the id to
   * {@link WitnessSource.notice} and the town's own board says who was taken and
   * what for. A client that reports what happened to somebody while they were
   * out cold reports the log's sentence and writes none of it.
   *
   * **The last taking rather than the first**, because unlike a death an arrest
   * can happen more than once to one person — so a caller that wants *what
   * happened just now* compares this against what it read before the dark, which
   * is the only honest way to date it from here.
   *
   * `null` for everybody the watch has never had hold of.
   */
  arrestOf(figure: FigureId): EventId | null;

  /**
   * What the town holds against a figure and has not answered, as
   * `sim_core::history::CrimeKind`'s own id, or `null` for nothing.
   *
   * **The blotter as a read** (decision 11): the gravest open row, which is the
   * same row the watch's own dispatch would answer — so a client that says why a
   * guard is walking over is reading the simulation's decision rather than
   * inventing one.
   *
   * It answers for the **player's** figure too, which is the case it exists for:
   * the player has no body, so the watch walks to the scene of the theft rather
   * than to them, and this is what turns a guard standing there into a sentence.
   */
  wantedFor(figure: FigureId): number | null;

  /**
   * What a crime is called: *theft*, *killing*, *abduction*.
   *
   * The simulation's own word ({@link WitnessSource.conditionWord}'s twin, and
   * for its reason): a client that authored its own would be keeping a copy of a
   * table it does not own (devlog #2). The empty string for an id this build's
   * simulation does not have.
   */
  crimeWord(crime: number): string;

  /*
   * The three **before the press** reads (chunk Fx-2).
   *
   * {@link WitnessSource.workAt}'s family — *the refusal's read* — one ruling
   * on: a prompt whose press would be a crime says so, in red, with the crime
   * word on it (the owner, 2026-08-31). The client keeps no crime table and
   * learns no legal code; it asks the wall what this press would be and prints
   * the noun that comes back.
   *
   * Both crime reads run the verb's own judgement **minus the witness clause**.
   * Whether anybody saw decides whether the town *knows* (`sim_core`'s `watch`);
   * the act is a crime either way, and a label that only warned where somebody
   * was watching would teach a player to check for witnesses rather than to know
   * what they are about to do.
   */

  /**
   * What taking whatever is on a tile would put on the player's blotter, as
   * `sim_core::history::CrimeKind`'s own id, or `null` for nothing.
   *
   * `Command::Take`'s own pick and its own two clauses: **carrying off somebody
   * you are no kin to** is abduction and **walking off with what is somebody
   * else's** is theft, with the body picked before the item because that is the
   * order the verb picks in.
   *
   * **Asking is not deciding** ({@link WitnessSource.workAt}'s rule): the tile is
   * re-read at the tick the command lands on.
   *
   * `null` before a claim, for a tile with nothing on it a pair of hands would
   * lift, for what nobody owns — dig spoil, a felled log, a gathered plant — for
   * the player's own things, and for kin.
   */
  crimeOfTaking(pos: TilePos): number | null;

  /**
   * What a town would hold against the player for swinging at somebody, as a
   * crime id, or `null` for nothing.
   *
   * {@link WitnessSource.crimeOfTaking}'s twin one verb over, and it answers
   * about **the act**: the id that comes back is the mildest row a landed blow
   * writes, because the graver two are what the blow turns out to *do* and a
   * client may not compute that (the phase's rule — a damage number here is a bug
   * by definition).
   *
   * `null` before a claim, for a watchman already engaged with the person he
   * would be swinging at, and for a body the world cannot place.
   */
  crimeOfStriking(figure: FigureId): number | null;

  /**
   * Whether a figure is one of the promoted town's watch.
   *
   * **The captain, or somebody the board has on a patrol** (`sim_core`'s
   * `watch`), which is the predicate the answering pass asks before it lets
   * anybody take hold of a culprit. It is read so that the moment this client
   * starts telling the simulation where the player is standing is a moment the
   * simulation would send somebody (`witness/law.ts`'s `needsMyTile`).
   *
   * **Wider than the board read it replaced**, and that is the point: a town's
   * captain holds no patrol order and a hand the watch has sent somewhere has
   * left his post, so a client filtering the board for patrol rows was watching
   * for the one watchman least likely to be the one coming.
   *
   * `false` in a world with nothing promoted, which is most of them.
   */
  isWatchman(figure: FigureId): boolean;

  /** The buildings a settlement has standing, in the plan's own order. */
  lots(settlement: SettlementId): readonly TownLot[];

  /** What a kind of building is called: *smithy*, *library*, *tavern*. */
  lotName(kind: number): string;

  /**
   * What a kind of work is called: *mine*, *fell*, *forge*, *patrol*.
   *
   * `sim_core::JobKind`'s own word for one of the `JOB_*` ids, and the empty
   * string for an id this build does not ship. {@link WitnessSource.lotName}'s
   * sibling one vocabulary over and read the same way — asked for rather than
   * kept, so this client holds no copy of a table it does not own.
   *
   * **What it is not is a sentence about an order.** The simulation composes no
   * line for a piece of work — no *"a tree to be felled by the north gate"* —
   * and the wasm export's own documentation still calls this word a debug label
   * (`crates/sim-wasm`'s `job_kind_name`). A board that says *fell* beside the
   * building the work is done in says what the town wants in the town's own
   * nouns, which is as far as the composing rule lets a client go until that
   * sentence exists sim-side. Filed by chunk U-2 with the export named.
   */
  jobWord(kind: number): string;

  /**
   * The books on the shelf standing at a tile, in handle order.
   *
   * Empty for a tile with no shelf on it **and** for a shelf nobody has written
   * a book onto, which are two different things — {@link WitnessSource.hasShelf}
   * is what tells them apart, and the difference is the whole of the empty-shelf
   * invitation (Phase 7 plan, decision 14).
   */
  shelved(pos: TilePos): readonly ShelvedBook[];

  /** Whether a shelf is standing on a tile at all. See {@link WitnessSource.shelved}. */
  hasShelf(pos: TilePos): boolean;

  /**
   * Which kind of gathering a tile is a site for, or `null` for none.
   *
   * **The refusal's read** (`sim_core::Snapshot::work_at`, wave 1.5's graybox
   * ruling: *every refused action displays a judgeable message*). The wave-1
   * playtest found `G` doing nothing at all outside town with nothing said about
   * it, which is indistinguishable from a broken key.
   *
   * **The judgement is the simulation's and the sentence is the client's**,
   * which is the same bargain {@link TownLot.trade} makes for the workshop verb.
   * A client that decided for itself what a tile was worth gathering would be
   * keeping a copy of the shape and variant vocabulary, and that is the thing
   * above this seam that is never allowed (devlog #2).
   *
   * **Asking is not deciding.** It is a read taken before a keystroke, not an
   * answer the command gives back: a tile that changes between the ask and the
   * tick is refused in silence exactly as it always was.
   *
   * The value is one of the `JOB_*` ids.
   */
  workAt(pos: TilePos): number | null;

  /**
   * For each column asked about, the nearest one a settlement's plan does not
   * speak for and nothing is growing on — or `null` in that slot for none
   * nearby.
   *
   * **The prop-placement read** (`sim_core::Snapshot::open_ground_near`, wave
   * 1.5's graybox ruling — *nothing stands in a street*). Chronicle stands,
   * market boards and graves are placed from a *record's* position, and a record
   * knows nothing about the town that grew around it: the playtest found them
   * standing in roads and inside building footprints.
   *
   * What the plan claims, what is growing there, and which candidate is nearest
   * are all facts the town plan and the world fields hold and this client does
   * not — so it asks rather than reconstructing them from
   * {@link WitnessSource.lots}.
   *
   * **A column and not a tile.** How high the ground is stays this client's own
   * question ({@link TerrainSampler.groundLevel} and the footing probe over it),
   * because the read is a pure function of (seed, records) and answers about
   * ground that has not been generated yet — which is the property that makes a
   * prop stay put as the world streams in around a player walking towards it.
   *
   * **Asked in a batch**, because laying a town out is the expensive half of the
   * plan and reading a column of it is the cheap one: a caller with a dozen
   * props to place derives one plan and not a dozen. One answer per column, in
   * order.
   *
   * A `null` leaves that prop where the record put it, which is what the client
   * did before this read existed.
   */
  openGround(
    settlement: SettlementId,
    columns: readonly (readonly [number, number])[],
  ): readonly (readonly [number, number] | null)[];
}

/**
 * One work order on the promoted town's board.
 *
 * **The recordless layer's data** (Phase 7 plan, O11): a town's daily work is
 * seen and never logged, so this is the only place a client can learn what
 * anybody is doing — and there is deliberately nothing in the chronicle to check
 * it against.
 *
 * What it does not carry is how much work is left. That is a countdown a
 * renderer would have to interpolate to use, and *"he is chopping that tree"* is
 * the whole of what a scene needs to know (`sim_core::JobPost`).
 */
export interface JobPost {
  /** Which kind of work: one of the `JOB_*` ids. */
  readonly kind: number;

  /** The tile the work is done to. */
  readonly site: TilePos;

  /** Who has taken it, or `null` while it is still open. */
  readonly worker: FigureId | null;
}

/** One body the promoted town has standing on real ground. */
export interface PresentFigure {
  readonly figure: FigureId;

  /** Where they are standing, in world tiles. The simulation's own answer. */
  readonly at: TilePos;

  /**
   * Which life stage the simulation says this body is in: a stage id, or `-1`
   * for a body the records hold no birth date for.
   *
   * **Derived sim-side and never stored** (`sim_core::body`, and rider 2's
   * ruling): a pure function of when the figure was born, what the calendar says
   * now, and the race's own stage table. It rides the position rather than being
   * a read of its own because a renderer needs it to draw the body at all — how
   * tall ({@link WitnessSource.bodyScale}) and how fast
   * ({@link WitnessSource.walkTicksForStage}) — and a getter per figure per
   * frame is what this boundary's bulk rows exist to avoid.
   *
   * A number rather than a union, like every other id that crosses here: the
   * vocabulary is the simulation's and the words come back through
   * {@link WitnessSource.lifeStageWord}.
   */
  readonly stage: number;

  /**
   * Who is carrying this body, or `null` if it is standing on its own feet.
   *
   * The held relation across the wall (`sim_core::local`'s `Presence::held_by`,
   * wave 2A). **The carrier may be the player**, who has no body in the
   * simulation at all — so a client draws a held body at whoever is holding it
   * and, for the player, that is the host's own position. The tile in
   * {@link PresentFigure.at} is where the body was lifted from until somebody
   * puts it down.
   */
  readonly heldBy: FigureId | null;
}

/**
 * One item, wherever it is: in the player's hands, or lying on a tile.
 *
 * One shape for both ends, because they are one layout across the wall — a
 * client decodes a pile and an inventory row through the same reader.
 *
 * The three ids are what a client groups, keys and counts by; the *words* are
 * {@link WitnessSource.itemLabel}'s, which is decision 11's split applied to the
 * one thing on this row that is prose.
 */
export interface CarriedItem {
  /**
   * The item's display number.
   *
   * **Good only while the item is.** Items are the one family of handle this
   * simulation removes, so this is a key for one read and never a name to file
   * away.
   */
  readonly item: number;

  /** What kind of thing it is: `sim_core::ItemKind`'s own id, DF's `item_type`. */
  readonly kind: number;

  /** What it is made of: an index into the material table. */
  readonly material: number;

  /** How well it was made: `sim_core::Quality`'s own id, 0 for anything nobody made. */
  readonly quality: number;
}

/** One building a town has actually put up. */
export interface TownLot {
  /** What the building is for: `sim_core::LotKind`'s own id. */
  readonly kind: number;

  /**
   * The trade practised at its bench, or `null` for a building nobody works in.
   *
   * **The workshop verb's gate, and it is the simulation's judgement.** A home
   * and a storehouse have a work tile like every other lot and no work at
   * either, so a client that offered the verb at every doorstep would be
   * offering a press the world refuses in silence — which is a verb that looks
   * broken.
   */
  readonly trade: number | null;

  /** The street column outside the door: where somebody stands when they knock. */
  readonly doorstep: { readonly x: number; readonly y: number };

  /**
   * The room column just inside the door, where a worker stands.
   *
   * The same column {@link WorldSource.craft} is sent to and the same one the
   * marketplace posts its own orders at, which is what makes the offer a client
   * draws and the command it sends one row.
   */
  readonly work: { readonly x: number; readonly y: number };
}

/** One book on a shelf: the handle, and how many records it cites. */
export interface ShelvedBook {
  /** The item's display number. {@link CarriedItem.item}'s caveat applies. */
  readonly item: number;

  /** How many records it cites. What a shelf is read for at a glance. */
  readonly citations: number;
}

/**
 * Returns the map key for a chunk coordinate.
 *
 * A string key rather than a nested map: three small integers, one lookup, and a
 * key that is readable in a debugger. The format is not parsed anywhere — it is
 * an identity, not a serialisation.
 */
export function chunkKey(coord: ChunkCoord): string {
  return `${coord.cx},${coord.cy},${coord.z}`;
}

/** Returns the chunk containing a tile. */
export function chunkOfTile(x: number, y: number, z: number): ChunkCoord {
  return { cx: floorDiv(x, CHUNK_SIZE), cy: floorDiv(y, CHUNK_SIZE), z };
}

/** Returns the tile coordinate of a chunk's `(0, 0)` corner. */
export function chunkOrigin(coord: ChunkCoord): TilePos {
  return { x: coord.cx * CHUNK_SIZE, y: coord.cy * CHUNK_SIZE, z: coord.z };
}

/**
 * Integer division that floors towards negative infinity.
 *
 * `Math.trunc` rounds towards zero, which puts tile -1 in chunk 0 alongside tile
 * 0 and folds the map at the origin. The bug is invisible until someone walks
 * west of the seed point, so it gets its own named function.
 */
export function floorDiv(value: number, divisor: number): number {
  return Math.floor(value / divisor);
}

/**
 * Returns `true` when two dates are the same day.
 *
 * The whole of the calendar arithmetic the client is allowed to do, alongside
 * {@link isOnOrBefore}: compare the year, then the day within it
 * ({@link WorldDateView}). Written down here rather than in each caller because
 * two of them decide whether something may be shown at all — a beat surfaces on
 * or after its own date and never before it (Phase 4 decision 1) — and a
 * comparison that is a rule deserves a name.
 */
export function isSameDate(left: WorldDateView, right: WorldDateView): boolean {
  return left.year === right.year && left.dayOfYear === right.dayOfYear;
}

/** Returns `true` when `left` is the same day as `right` or an earlier one. */
export function isOnOrBefore(left: WorldDateView, right: WorldDateView): boolean {
  return left.year < right.year || (left.year === right.year && left.dayOfYear <= right.dayOfYear);
}

/**
 * Throws unless the buffers satisfy the {@link ChunkMeshBuffers} contract.
 *
 * The contract is checked rather than trusted because its far side is generated
 * Rust behind a wasm ABI: when the mesher's layout drifts, this throw names the
 * chunk and the mismatch, instead of the GPU drawing garbage or nothing. Cheap —
 * a handful of comparisons per chunk, no per-vertex work except the index bound
 * scan, which is the one error that would otherwise read out of bounds.
 */
export function validateChunkMeshBuffers(coord: ChunkCoord, buffers: ChunkMeshBuffers): void {
  const { positions, normals, colors, indices } = buffers;
  const where = `chunk ${chunkKey(coord)}`;

  if (positions.length % 3 !== 0) {
    throw new Error(`${where}: positions length ${positions.length} is not a multiple of 3`);
  }

  if (normals.length !== positions.length) {
    throw new Error(
      `${where}: normals length ${normals.length} does not match positions ${positions.length}`,
    );
  }

  if (colors.length !== positions.length) {
    throw new Error(
      `${where}: colors length ${colors.length} does not match positions ${positions.length}`,
    );
  }

  if (indices.length % 3 !== 0) {
    throw new Error(`${where}: indices length ${indices.length} is not a multiple of 3`);
  }

  const vertexCount = positions.length / 3;

  for (let i = 0; i < indices.length; i += 1) {
    const index = indices[i] ?? 0;

    if (index >= vertexCount) {
      throw new Error(`${where}: index ${index} at ${i} exceeds vertex count ${vertexCount}`);
    }
  }
}

/** Returns `true` when a mesh has no triangles — a chunk of pure air or pure interior. */
export function isEmptyMesh(buffers: ChunkMeshBuffers): boolean {
  return buffers.indices.length === 0;
}
